// src/pages/MaintenancePage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Panel, Field, Input, Select, Button, DangerButton, Textarea } from '../ui';
import { ProposeChange } from '../graphqlOps';
import { useCountry } from '../context/CountryContext';
import {
  Wrench,
  Mail,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  Calendar,
  Pencil,
  Trash2,
  ShieldCheck,
  Copy,
  Settings,
  Droplets,
} from 'lucide-react';

const client = createAuditedClient('MaintenancePage');
const rawClient = generateClient();

/* ===================== Constants & Presets ===================== */
const SERVICE_PRESETS = [
  {
    value: 'OIL_CHANGE',
    label: 'Oil Change',
    defaultIntervalKm: 8000,
    defaultIntervalMonths: 6,
  },
  {
    value: 'OIL_AND_FILTER',
    label: 'Oil & Filter Change',
    defaultIntervalKm: 10000,
    defaultIntervalMonths: 6,
  },
  {
    value: 'BRAKES',
    label: 'Brake Pads / Discs Check',
    defaultIntervalKm: 25000,
    defaultIntervalMonths: 12,
  },
  {
    value: 'TIRES',
    label: 'Tires / Rotation / Alignment',
    defaultIntervalKm: 20000,
    defaultIntervalMonths: 12,
  },
  {
    value: 'AIR_CABIN_FILTER',
    label: 'Air & Cabin Filter',
    defaultIntervalKm: 15000,
    defaultIntervalMonths: 12,
  },
  {
    value: 'TRANSMISSION_FLUID',
    label: 'Transmission Fluid',
    defaultIntervalKm: 40000,
    defaultIntervalMonths: 24,
  },
  {
    value: 'BATTERY',
    label: 'Battery Check / Replacement',
    defaultIntervalKm: 30000,
    defaultIntervalMonths: 12,
  },
  {
    value: 'COOLANT',
    label: 'Coolant & Fluids Check',
    defaultIntervalKm: 20000,
    defaultIntervalMonths: 12,
  },
  {
    value: 'GENERAL_INSPECTION',
    label: 'General Mechanical Check',
    defaultIntervalKm: 10000,
    defaultIntervalMonths: 6,
  },
  {
    value: 'CUSTOM',
    label: 'Custom Service / Repair',
    defaultIntervalKm: 10000,
    defaultIntervalMonths: 6,
  },
];

const SERVICE_LABEL_BY_VALUE = Object.fromEntries(
  SERVICE_PRESETS.map((p) => [p.value, p.label])
);

const COLORS = {
  primary: '#2563eb',
  primaryDark: '#1e40af',
  primaryLight: '#eff6ff',
  lime: '#83d466',
  limeDark: '#166534',
  limeLight: '#dcfce7',
  success: '#10b981',
  successLight: '#d1fae5',
  danger: '#ef4444',
  dangerLight: '#fee2e2',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
};

/* ===================== GraphQL Operations ===================== */
const ListCompanies = /* GraphQL */ `
  query ListCompanies {
    listCompanies(limit: 1000) {
      items {
        id
        name
        country
        createdAt
      }
      nextToken
    }
  }
`;

const GetCompany = /* GraphQL */ `
  query GetCompany($id: ID!) {
    getCompany(id: $id) {
      id
      name
      country
    }
  }
`;

const ListCars = /* GraphQL */ `
  query ListCars($filter: ModelCarFilterInput, $limit: Int, $nextToken: String) {
    listCars(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        CompanyId
        name
        plate
        active
        fuelType
        transmission
        year
        category
        bodyType
        color
        imageKey
        startingPrice
        seats
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const ListMaintenances = /* GraphQL */ `
  query ListMaintenances(
    $filter: ModelMaintenanceFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listMaintenances(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        CompanyId
        carId
        title
        start
        end
        notes
        approvalStatus
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const MaintenanceByCompanyByStart = /* GraphQL */ `
  query MaintenanceByCompanyByStart(
    $CompanyId: String!
    $limit: Int
    $nextToken: String
  ) {
    maintenanceByCompanyByStart(
      CompanyId: $CompanyId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        CompanyId
        carId
        title
        start
        end
        notes
        approvalStatus
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const CreateMaintenance = /* GraphQL */ `
  mutation CreateMaintenance($input: CreateMaintenanceInput!) {
    createMaintenance(input: $input) {
      id
      CompanyId
      carId
      title
      start
      end
      notes
      approvalStatus
      createdAt
      updatedAt
    }
  }
`;

const UpdateMaintenance = /* GraphQL */ `
  mutation UpdateMaintenance($input: UpdateMaintenanceInput!) {
    updateMaintenance(input: $input) {
      id
      CompanyId
      carId
      title
      start
      end
      notes
      approvalStatus
      createdAt
      updatedAt
    }
  }
`;

const DeleteMaintenance = /* GraphQL */ `
  mutation DeleteMaintenance($input: DeleteMaintenanceInput!) {
    deleteMaintenance(input: $input) {
      id
    }
  }
`;

const HandoverByCompanyByCreatedAt = /* GraphQL */ `
  query HandoverByCompanyByCreatedAt(
    $CompanyId: String!
    $sortDirection: ModelSortDirection
    $limit: Int
  ) {
    handoverByCompanyByCreatedAt(
      CompanyId: $CompanyId
      sortDirection: $sortDirection
      limit: $limit
    ) {
      items {
        id
        carId
        carPlate
        odometerKm
        createdAt
      }
    }
  }
`;

const SubmitInboundRequestMutation = /* GraphQL */ `
  mutation SubmitInboundRequest($input: SubmitInboundRequestInput!) {
    submitInboundRequest(input: $input) {
      id
      type
      status
      payload
      createdAt
    }
  }
`;

/* ===================== Helpers ===================== */
function useClaims() {
  const [claims, setClaims] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await getCurrentUser();
        const { tokens } = await fetchAuthSession({ forceRefresh: false });
        const p = tokens?.idToken?.payload || {};
        const CompanyId = p['custom:CompanyId'] ?? p['custom:companyId'] ?? null;
        const groups = p['cognito:groups'] || [];
        if (!alive) return;
        setClaims({
          CompanyId,
          groups,
          sub: p.sub || null,
          email: p.email || null,
          name: p.name || null,
        });
      } catch (e) {
        console.error('MaintenancePage: fetchAuthSession failed', e);
        if (!alive) return;
        setClaims({ CompanyId: null, groups: [] });
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return { claims, ready };
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    if (media.addEventListener) media.addEventListener('change', onChange);
    else media.addListener(onChange);
    setMatches(media.matches);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', onChange);
      else media.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

function normalizePlate(raw) {
  return String(raw || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

function currencyForCountry(country) {
  const c = String(country || '').toUpperCase();
  if (c === 'GE' || c === 'GEORGIA') return 'USD';
  if (c === 'AL' || c === 'ALBANIA') return 'EUR';
  return 'USD';
}

function todayYmd() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function addMonthsToYmd(ymd, months) {
  const base = ymd ? new Date(`${ymd}T12:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return todayYmd();
  base.setMonth(base.getMonth() + Number(months || 0));
  const yyyy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, '0');
  const dd = String(base.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function toYmd(dateLike) {
  if (!dateLike) return '';
  const str = String(dateLike).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Parses Greenway human-readable dates like "6 August, 2027" or "22 March, 2028"
 * into "YYYY-MM-DD" format.
 */
function parseGreenwayDateToYmd(rawDateStr) {
  if (!rawDateStr) return '';
  const cleaned = String(rawDateStr).replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) return cleaned;

  const MONTHS = {
    january: '01',
    february: '02',
    march: '03',
    april: '04',
    may: '05',
    june: '06',
    july: '07',
    august: '08',
    september: '09',
    october: '10',
    november: '11',
    december: '12',
  };

  // Match "6 August 2027"
  const m1 = cleaned.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (m1) {
    const day = String(m1[1]).padStart(2, '0');
    const mon = MONTHS[m1[2].toLowerCase()];
    const year = m1[3];
    if (mon) return `${year}-${mon}-${day}`;
  }

  // Match "August 6 2027"
  const m2 = cleaned.match(/^([A-Za-z]+)\s+(\d{1,2})\s+(\d{4})$/);
  if (m2) {
    const mon = MONTHS[m2[1].toLowerCase()];
    const day = String(m2[2]).padStart(2, '0');
    const year = m2[3];
    if (mon) return `${year}-${mon}-${day}`;
  }

  // Fallback to native Date
  const parsed = new Date(cleaned);
  if (!Number.isNaN(parsed.getTime())) {
    return toYmd(parsed);
  }
  return '';
}

/**
 * Extracts details from Greenway's check-date response payload.
 */
function parseGreenwayResponse(gwData) {
  const data = gwData?.Data || gwData?.data || gwData || {};
  const modalHtml = String(data.ModalHtml || '');
  const resultType = Number(data.ResultType || 0);

  let nextRaw = data.NextInspectionDate || '';
  if (!nextRaw) {
    const attrMatch = modalHtml.match(/data-next-inspection-date="([^"]+)"/i);
    if (attrMatch?.[1]) nextRaw = attrMatch[1].trim();
  }

  let deadlineDate = parseGreenwayDateToYmd(nextRaw);

  // Try to extract last inspection date (and fallback deadline) from ModalHtml timeline
  let lastInspectionDate = '';
  const timelineMatches = [
    ...modalHtml.matchAll(
      /<span[^>]*class="item-title"[^>]*>([^<]+)<\/span>\s*<p>([^<]+)<\/p>/gi
    ),
  ];
  for (const m of timelineMatches) {
    const dtText = (m[1] || '').trim();
    const desc = (m[2] || '').trim().toLowerCase();
    if (desc.includes('passed')) {
      lastInspectionDate = parseGreenwayDateToYmd(dtText) || dtText;
    } else if (
      !deadlineDate &&
      (desc.includes('next') ||
        desc.includes('first inspection') ||
        desc.includes('overdue') ||
        desc.includes('deadline'))
    ) {
      const parsedDt = parseGreenwayDateToYmd(dtText);
      if (parsedDt) {
        deadlineDate = parsedDt;
        if (!nextRaw) nextRaw = dtText;
      }
    }
  }

  const isNotFound =
    resultType === 6 ||
    data.IsVehiclePlateValid === false ||
    (data.IsVehiclePlateValid == null && !deadlineDate);

  const isVehiclePlateValid = !isNotFound && (data.IsVehiclePlateValid !== false);
  const isOverdueOnGreenway =
    !isNotFound &&
    (data.IsResultTypeSuccess === false ||
      resultType === 4 ||
      /Vehicle Overdue/i.test(modalHtml));

  const isResultTypeSuccess = isNotFound ? true : !isOverdueOnGreenway;

  const isUnder4Years =
    !isNotFound && (resultType === 2 || /under\s+4\s+years/i.test(modalHtml));

  let statusText = 'Valid';
  if (isNotFound) {
    statusText = 'Not found on Greenway';
  } else if (isOverdueOnGreenway) {
    statusText = 'Overdue on Greenway';
  } else if (isUnder4Years) {
    statusText = 'Exempt (< 4 years old)';
  } else if (deadlineDate) {
    statusText = 'Passed / Active';
  }

  return {
    isValid: isVehiclePlateValid && (Boolean(deadlineDate) || isOverdueOnGreenway || isUnder4Years),
    notFound: isNotFound,
    deadlineDate,
    deadlineDisplay: nextRaw,
    lastInspectionDate,
    isResultTypeSuccess,
    isExempt: isUnder4Years,
    resultType,
    statusText,
  };
}

function isLocalDevHost() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

/**
 * Calls Greenway PTI check endpoint using:
 * 1) Local dev proxy (/api/greenway/check-date) when running on localhost
 * 2) Backend Lambda (submitInboundRequest with source="greenway_check")
 * 3) Public CORS proxy fallback
 */
async function fetchGreenwayDeadlineByPlate(rawPlate, companyId) {
  const plate = normalizePlate(rawPlate);
  if (!plate) {
    throw new Error('Car has no valid alphanumeric license plate.');
  }

  // 1) Try local dev middleware (/api/greenway/check-date) on localhost
  if (isLocalDevHost()) {
    try {
      const res = await fetch(`/api/greenway/check-date?plate=${encodeURIComponent(plate)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const contentType = res.headers.get('content-type') || '';
      if (res.ok && contentType.includes('application/json')) {
        const json = await res.json();
        if (json?.ok && json?.greenway) {
          return parseGreenwayResponse(json.greenway);
        }
      }
    } catch {
      // Fall through to next method
    }
  }

  // 2) Try backend Lambda via GraphQL (unaudited rawClient to avoid AuditEvent spam)
  try {
    const gqlRes = await rawClient.graphql({
      query: SubmitInboundRequestMutation,
      variables: {
        input: {
          CompanyId: companyId || null,
          type: 'CONTACT_LEAD',
          name: plate,
          selectedCarPlate: plate,
          source: 'greenway_check',
          payload: JSON.stringify({ kind: 'GREENWAY_CHECK', plate }),
        },
      },
      authMode: 'userPool',
    });
    const returnedPayload = tryParseJson(
      gqlRes?.data?.submitInboundRequest?.payload
    );
    if (returnedPayload?.ok && returnedPayload?.greenway) {
      return parseGreenwayResponse(returnedPayload.greenway);
    }
  } catch (err) {
    console.warn('Greenway Lambda lookup error for plate', plate, err);
  }

  // 3) Fallback: CORS proxy to https://greenway.ge/en/check-date
  const formBody = `vehicleLicensePlate=${encodeURIComponent(plate)}`;
  const proxyUrls = [
    'https://corsproxy.io/?https://greenway.ge/en/check-date',
    'https://thingproxy.freeboard.io/fetch/https://greenway.ge/en/check-date',
  ];

  for (const url of proxyUrls) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          Accept: 'application/json',
        },
        body: formBody,
      });
      if (res.ok) {
        const json = await res.json();
        if (json && (json.IsSuccess !== undefined || json.Data)) {
          return parseGreenwayResponse(json);
        }
      }
    } catch {
      // try next
    }
  }

  throw new Error(
    `Could not auto-fetch plate ${plate} from greenway.ge. You can check directly on greenway.ge and set the deadline date manually.`
  );
}

/**
 * Fetches multiple Georgian plates in a single backend Lambda invocation
 * (with automatic per-plate fallback if batch mode is unavailable).
 * Returns a Map<normalizedPlate, parsedGreenwayResult>.
 */
async function fetchGreenwayDeadlinesBatch(rawPlates, companyId) {
  const plates = Array.from(
    new Set((rawPlates || []).map(normalizePlate).filter(Boolean))
  );
  const out = new Map();
  if (plates.length === 0) return out;

  // On localhost dev server, hit local setupProxy in small parallel chunks
  if (isLocalDevHost()) {
    const CHUNK = 5;
    for (let i = 0; i < plates.length; i += CHUNK) {
      const slice = plates.slice(i, i + CHUNK);
      await Promise.all(
        slice.map(async (plate) => {
          try {
            const res = await fetch(
              `/api/greenway/check-date?plate=${encodeURIComponent(plate)}`,
              { method: 'GET', headers: { Accept: 'application/json' } }
            );
            const contentType = res.headers.get('content-type') || '';
            if (res.ok && contentType.includes('application/json')) {
              const json = await res.json();
              if (json?.ok && json?.greenway) {
                out.set(plate, parseGreenwayResponse(json.greenway));
              }
            }
          } catch {
            // ignore
          }
        })
      );
    }
    if (out.size === plates.length) {
      return out;
    }
  }

  // Try single-invocation batch check via backend Lambda (chunks of up to 25 plates)
  const BATCH_SIZE = 25;
  let batchSupported = true;
  for (let i = 0; i < plates.length; i += BATCH_SIZE) {
    const chunk = plates.slice(i, i + BATCH_SIZE).filter((p) => !out.has(p));
    if (chunk.length === 0) continue;

    try {
      const gqlRes = await rawClient.graphql({
        query: SubmitInboundRequestMutation,
        variables: {
          input: {
            CompanyId: companyId || null,
            type: 'CONTACT_LEAD',
            name: `BATCH_${chunk.length}`,
            selectedCarPlate: chunk[0],
            source: 'greenway_batch_check',
            payload: JSON.stringify({
              kind: 'GREENWAY_BATCH_CHECK',
              plates: chunk,
            }),
          },
        },
        authMode: 'userPool',
      });

      const returnedPayload = tryParseJson(
        gqlRes?.data?.submitInboundRequest?.payload
      );
      if (returnedPayload?.ok && returnedPayload?.batch && returnedPayload?.results) {
        for (const [pl, item] of Object.entries(returnedPayload.results)) {
          if (item?.ok && item?.greenway) {
            out.set(normalizePlate(pl), parseGreenwayResponse(item.greenway));
          }
        }
      } else {
        batchSupported = false;
        break;
      }
    } catch (err) {
      console.warn('Greenway batch lookup fallback triggered:', err);
      batchSupported = false;
      break;
    }
  }

  // Fallback: parallel per-plate lookup for any remaining plates
  const missingPlates = batchSupported
    ? []
    : plates.filter((p) => !out.has(p));

  if (missingPlates.length > 0) {
    const CONCURRENCY = 4;
    for (let i = 0; i < missingPlates.length; i += CONCURRENCY) {
      const slice = missingPlates.slice(i, i + CONCURRENCY);
      await Promise.all(
        slice.map(async (pl) => {
          try {
            const res = await fetchGreenwayDeadlineByPlate(pl, companyId);
            if (res) out.set(pl, res);
          } catch {
            // handled by caller
          }
        })
      );
    }
  }

  return out;
}

/**
 * Safely parses JSON values including double-encoded AWSJSON strings returned by AppSync.
 */
function tryParseJson(raw) {
  if (!raw) return null;
  let cur = raw;
  for (let i = 0; i < 4; i++) {
    if (cur && typeof cur === 'object') return cur;
    if (typeof cur !== 'string') return null;
    const trimmed = cur.trim();
    if (!trimmed) return null;
    try {
      cur = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return cur && typeof cur === 'object' ? cur : null;
}

function daysUntilYmd(ymd) {
  if (!ymd) return null;
  const target = new Date(`${toYmd(ymd)}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = target.getTime() - todayStart.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

function formatDateShort(d) {
  if (!d) return '—';
  const ymd = toYmd(d);
  if (!ymd) return String(d);
  try {
    const date = new Date(`${ymd}T12:00:00`);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return ymd;
  }
}

function formatDateTime(d) {
  if (!d) return '—';
  try {
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return String(d);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(d);
  }
}

/**
 * Evaluates urgency of a Service Log based on BOTH date and mileage.
 */
function evaluateServiceLogStatus(log, carCurrentKm, defaultLeadDays = 14, defaultLeadKm = 500) {
  if (log.status === 'COMPLETED') {
    return {
      code: 'COMPLETED',
      label: 'Completed',
      urgencyScore: 999999,
      daysRemaining: null,
      kmRemaining: null,
      reasons: ['Marked as completed'],
    };
  }

  const leadDays = Number(log.reminderLeadDays ?? defaultLeadDays ?? 14);
  const leadKm = Number(log.reminderLeadKm ?? defaultLeadKm ?? 500);
  const effectiveCurrentKm = Number(carCurrentKm || log.currentKm || 0) || null;
  const nextDueKm = Number(log.nextDueKm || 0) || null;

  const daysRem = log.nextDueDate ? daysUntilYmd(log.nextDueDate) : null;
  const kmRem =
    effectiveCurrentKm != null && nextDueKm != null
      ? nextDueKm - effectiveCurrentKm
      : null;

  const reasons = [];
  let isOverdue = false;
  let isDueSoon = false;

  if (daysRem != null) {
    if (daysRem < 0) {
      isOverdue = true;
      reasons.push(`Overdue by ${Math.abs(daysRem)} day${Math.abs(daysRem) === 1 ? '' : 's'}`);
    } else if (daysRem === 0) {
      isOverdue = true;
      reasons.push('Due today');
    } else if (daysRem <= leadDays) {
      isDueSoon = true;
      reasons.push(`Due in ${daysRem} day${daysRem === 1 ? '' : 's'}`);
    } else {
      reasons.push(`In ${daysRem} days`);
    }
  }

  if (kmRem != null) {
    if (kmRem <= 0) {
      isOverdue = true;
      reasons.push(`${Math.abs(kmRem).toLocaleString()} km past due`);
    } else if (kmRem <= leadKm) {
      isDueSoon = true;
      reasons.push(`${kmRem.toLocaleString()} km remaining`);
    } else {
      reasons.push(`${kmRem.toLocaleString()} km left`);
    }
  }

  // Compute a combined chronological sort score (lower = more urgent)
  const dayScore = daysRem != null ? daysRem : 3650;
  const kmEquivalentDays = kmRem != null ? Math.round(kmRem / 100) : 3650;
  const urgencyScore = Math.min(dayScore, kmEquivalentDays);

  if (isOverdue) {
    return {
      code: 'OVERDUE',
      label: 'Overdue',
      urgencyScore: Math.min(-1, urgencyScore),
      daysRemaining: daysRem,
      kmRemaining: kmRem,
      reasons,
    };
  }

  if (isDueSoon) {
    return {
      code: 'DUE_SOON',
      label: 'Due Soon',
      urgencyScore,
      daysRemaining: daysRem,
      kmRemaining: kmRem,
      reasons,
    };
  }

  return {
    code: 'UPCOMING',
    label: 'Scheduled / OK',
    urgencyScore,
    daysRemaining: daysRem,
    kmRemaining: kmRem,
    reasons: reasons.length ? reasons : ['Scheduled'],
  };
}

/**
 * Evaluates Greenway PTI deadline status for a Georgian car.
 */
function evaluateGreenwayStatus(record, leadDays = 14) {
  if (!record) {
    return {
      code: 'UNCHECKED',
      label: 'Needs Check',
      daysRemaining: null,
      sortKey: 9999999,
      detail: 'No deadline recorded yet — syncing from greenway.ge…',
    };
  }

  if (!record.deadlineDate && !record.isExempt && record.isResultTypeSuccess !== false) {
    const isNotFound =
      record.notFoundOnGreenway || /not found/i.test(record.statusText || '');
    return {
      code: 'UNCHECKED',
      label: isNotFound ? 'Unverified Plate' : 'Needs Check',
      daysRemaining: null,
      sortKey: 9999999,
      detail: isNotFound
        ? 'Plate not found on greenway.ge — set deadline manually if needed'
        : 'No deadline recorded yet — sync or check on greenway.ge',
    };
  }

  const daysRem = record.deadlineDate ? daysUntilYmd(record.deadlineDate) : null;

  if (record.isResultTypeSuccess === false || (daysRem != null && daysRem < 0)) {
    const overdueDays = daysRem != null ? Math.abs(daysRem) : 1;
    return {
      code: 'OVERDUE',
      label: 'Overdue',
      daysRemaining: daysRem,
      sortKey: daysRem != null ? daysRem : -9999,
      detail:
        daysRem != null
          ? `Overdue by ${overdueDays} day${overdueDays === 1 ? '' : 's'}`
          : 'Inspection required immediately',
    };
  }

  if (daysRem === 0) {
    return {
      code: 'OVERDUE',
      label: 'Due Today',
      daysRemaining: 0,
      sortKey: 0,
      detail: 'Deadline is today!',
    };
  }

  if (daysRem != null && daysRem <= Number(leadDays || 14)) {
    return {
      code: 'DUE_SOON',
      label: 'Due Soon',
      daysRemaining: daysRem,
      sortKey: daysRem,
      detail: `Due in ${daysRem} day${daysRem === 1 ? '' : 's'}`,
    };
  }

  if (record.isExempt) {
    return {
      code: 'EXEMPT',
      label: 'Exempt (< 4 yrs)',
      daysRemaining: daysRem,
      sortKey: daysRem != null ? daysRem : 888888,
      detail:
        daysRem != null
          ? `First inspection in ${daysRem} days`
          : 'Vehicle under 4 years old',
    };
  }

  return {
    code: 'VALID',
    label: 'Valid',
    daysRemaining: daysRem,
    sortKey: daysRem != null ? daysRem : 777777,
    detail: daysRem != null ? `Due in ${daysRem} days` : 'Valid',
  };
}

/* ===================== Styles ===================== */
function cardStyle(extra = {}) {
  return {
    border: `1px solid ${COLORS.gray[200]}`,
    borderRadius: 16,
    padding: 20,
    background: '#ffffff',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.04)',
    ...extra,
  };
}

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 10,
  border: `1px solid ${COLORS.gray[300]}`,
  fontSize: 13.5,
  outline: 'none',
  boxSizing: 'border-box',
  background: '#ffffff',
  color: COLORS.gray[900],
  fontFamily: 'inherit',
};

const selectStyle = {
  ...inputStyle,
  cursor: 'pointer',
};

const labelStyle = {
  display: 'block',
  fontSize: 12.5,
  fontWeight: 700,
  color: COLORS.gray[700],
  marginBottom: 5,
};

function statusBadgeStyle(code) {
  switch (code) {
    case 'OVERDUE':
      return {
        background: '#fee2e2',
        color: '#991b1b',
        border: '1px solid #fca5a5',
      };
    case 'DUE_SOON':
      return {
        background: '#fef3c7',
        color: '#92400e',
        border: '1px solid #fcd34d',
      };
    case 'VALID':
    case 'UPCOMING':
    case 'COMPLETED':
      return {
        background: '#dcfce7',
        color: '#166534',
        border: '1px solid #86efac',
      };
    case 'EXEMPT':
      return {
        background: '#e0f2fe',
        color: '#075985',
        border: '1px solid #7dd3fc',
      };
    default:
      return {
        background: '#f3f4f6',
        color: '#374151',
        border: '1px solid #d1d5db',
      };
  }
}

/* ===================== Main Page Component ===================== */
export default function MaintenancePage() {
  const { claims, ready } = useClaims();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const {
    country,
    setCountry,
    selectedCompanyId: ctxCompanyId,
    loadingCompany,
  } = useCountry();

  const groups = claims?.groups || [];
  const isAdmin = groups.includes('admin');
  const isCountryAdmin = groups.includes('country_admin');
  const isEditor = groups.includes('editor');
  const canEdit = isAdmin || isCountryAdmin || isEditor;
  const myCompanyId = claims?.CompanyId || null;

  const activeCompanyId = useMemo(
    () => (isAdmin ? ctxCompanyId || myCompanyId || null : myCompanyId || null),
    [isAdmin, ctxCompanyId, myCompanyId]
  );

  // Active section tab: 'logs' | 'greenway' | 'schedule'
  const [activeTab, setActiveTab] = useState('logs');

  // Companies & Fleet
  const [companies, setCompanies] = useState([]);
  const [cars, setCars] = useState([]);
  const [georgiaCars, setGeorgiaCars] = useState([]);
  const [georgiaCompanyId, setGeorgiaCompanyId] = useState(null);
  const [carsLoading, setCarsLoading] = useState(false);

  // Odometer map (carId -> km) derived from HandoverForms + manual overrides
  const [handoverKmByCarId, setHandoverKmByCarId] = useState({});
  const [manualKmByCarId, setManualKmByCarId] = useState({});
  const [editingKmCarId, setEditingKmCarId] = useState(null);
  const [editingKmValue, setEditingKmValue] = useState('');

  // Service Logs (stored in Maintenance with CompanyId = `${activeCompanyId}#MAINT_LOG`)
  const [serviceLogs, setServiceLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logEditingId, setLogEditingId] = useState(null);
  const [logFilterCarId, setLogFilterCarId] = useState('ALL');
  const [logFilterType, setLogFilterType] = useState('ALL');
  const [logFilterStatus, setLogFilterStatus] = useState('ALL');

  const [logForm, setLogForm] = useState(() => ({
    carId: '',
    serviceType: 'OIL_CHANGE',
    customTitle: '',
    serviceDate: todayYmd(),
    nextDueDate: addMonthsToYmd(todayYmd(), 6),
    serviceKm: '',
    currentKm: '',
    nextDueKm: '',
    cost: '',
    currency: 'USD',
    workshop: '',
    notes: '',
    reminderEnabled: true,
    reminderLeadDays: 14,
    reminderLeadKm: 500,
    status: 'ACTIVE',
  }));

  // Greenway records (stored in Maintenance with CompanyId = `${georgiaCompanyId}#GREENWAY`)
  const [greenwayByCarId, setGreenwayByCarId] = useState({});
  const greenwayByCarIdRef = useRef({});
  const [loadedGreenwayCompanyId, setLoadedGreenwayCompanyId] = useState(null);
  const [greenwayLoading, setGreenwayLoading] = useState(false);
  const [syncingPlateCarId, setSyncingPlateCarId] = useState(null);
  const [syncingAllGreenway, setSyncingAllGreenway] = useState(false);
  const autoGreenwaySyncedForCompanyRef = useRef(null);
  const [editingGreenwayCar, setEditingGreenwayCar] = useState(null);
  const [greenwayForm, setGreenwayForm] = useState({
    deadlineDate: '',
    lastInspectionDate: '',
    isExempt: false,
    reminderEnabled: true,
    reminderLeadDays: 14,
    notes: '',
  });
  const [greenwaySearch, setGreenwaySearch] = useState('');

  // Email Reminder Settings (stored in Maintenance with CompanyId = `${activeCompanyId}#MAINT_SETTINGS`)
  const [settingsRecordId, setSettingsRecordId] = useState(null);
  const [reminderSettingsOpen, setReminderSettingsOpen] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderSettings, setReminderSettings] = useState({
    recipientEmails: '',
    autoSendEnabled: true,
    defaultLeadDays: 14,
    defaultLeadKm: 500,
    greenwayLeadDays: 14,
    lastAutoReminderSentAt: null,
  });

  // Calendar Maintenance Schedule (blocking calendar dates, moved from CarsPage)
  const [scheduleCarId, setScheduleCarId] = useState('');
  const [scheduleItems, setScheduleItems] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleEditingId, setScheduleEditingId] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    title: '',
    start: '',
    end: '',
    notes: '',
  });

  // Toast alert
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);
  const autoReminderCheckedRef = useRef(false);
  const logFormRef = useRef(null);

  const showAlert = useCallback((message, variant = 'info') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
    }, 4500);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const activeCompany = useMemo(
    () => companies.find((c) => c.id === activeCompanyId) || null,
    [companies, activeCompanyId]
  );

  const effectiveCountryName = useMemo(() => {
    if (activeCompany?.country) return activeCompany.country;
    if (country) return country;
    return 'Georgia';
  }, [activeCompany, country]);

  const isGeorgiaSelected = useMemo(() => {
    const c = String(effectiveCountryName || '').toLowerCase();
    return c.includes('georg') || c === 'ge';
  }, [effectiveCountryName]);

  const defaultCurrency = useMemo(
    () => currencyForCountry(effectiveCountryName),
    [effectiveCountryName]
  );

  /* ===================== Load Companies & Cars ===================== */
  const loadCompaniesAndFleet = useCallback(async () => {
    if (!ready) return;
    setCarsLoading(true);
    try {
      let companyList = [];
      if (isAdmin) {
        const compRes = await client.graphql({
          query: ListCompanies,
          authMode: 'userPool',
        });
        companyList = (compRes.data?.listCompanies?.items || []).filter(Boolean);
        setCompanies(companyList);
      } else if (myCompanyId) {
        const compRes = await client.graphql({
          query: GetCompany,
          variables: { id: myCompanyId },
          authMode: 'userPool',
        });
        const co = compRes.data?.getCompany;
        companyList = co ? [co] : [{ id: myCompanyId, name: myCompanyId, country }];
        setCompanies(companyList);
      }

      // Identify Georgia company for Greenway section
      const geCo = companyList.find((c) => {
        const cn = String(c.country || c.name || '').toLowerCase();
        return cn.includes('georg') || cn === 'ge';
      });
      const resolvedGeCompanyId =
        geCo?.id || (isGeorgiaSelected ? activeCompanyId : null);
      setGeorgiaCompanyId(resolvedGeCompanyId);

      // Load cars for activeCompanyId
      if (activeCompanyId) {
        const carsRes = await client.graphql({
          query: ListCars,
          variables: {
            filter: { CompanyId: { eq: activeCompanyId } },
            limit: 1000,
          },
          authMode: 'userPool',
        });
        const items = (carsRes.data?.listCars?.items || [])
          .filter(Boolean)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCars(items);

        if (resolvedGeCompanyId === activeCompanyId) {
          setGeorgiaCars(items);
        } else if (resolvedGeCompanyId) {
          const geCarsRes = await client.graphql({
            query: ListCars,
            variables: {
              filter: { CompanyId: { eq: resolvedGeCompanyId } },
              limit: 1000,
            },
            authMode: 'userPool',
          });
          const geItems = (geCarsRes.data?.listCars?.items || [])
            .filter(Boolean)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setGeorgiaCars(geItems);
        }
      }
    } catch (err) {
      console.error('MaintenancePage: failed to load fleet', err);
      showAlert('Failed to load country fleet.', 'error');
    } finally {
      setCarsLoading(false);
    }
  }, [ready, isAdmin, myCompanyId, activeCompanyId, country, isGeorgiaSelected, showAlert]);

  /* ===================== Load Latest Odometer from HandoverForms ===================== */
  const loadHandoverOdometers = useCallback(async (companyId) => {
    if (!companyId) return;
    try {
      const res = await client.graphql({
        query: HandoverByCompanyByCreatedAt,
        variables: {
          CompanyId: companyId,
          sortDirection: 'DESC',
          limit: 300,
        },
        authMode: 'userPool',
      });
      const forms = res.data?.handoverByCompanyByCreatedAt?.items || [];
      const kmMap = {};
      for (const f of forms) {
        if (!f || !f.carId || f.odometerKm == null) continue;
        const km = Number(f.odometerKm);
        if (Number.isFinite(km) && km > 0) {
          if (!kmMap[f.carId] || km > kmMap[f.carId]) {
            kmMap[f.carId] = km;
          }
        }
      }
      setHandoverKmByCarId(kmMap);
    } catch (err) {
      console.warn('MaintenancePage: handover odometer fetch skipped:', err?.message || err);
    }
  }, []);

  /* ===================== Load Service Logs, Settings & Greenway ===================== */
  const loadServiceLogsAndSettings = useCallback(
    async (companyId) => {
      if (!companyId) return;
      setLogsLoading(true);
      const logNamespace = `${companyId}#MAINT_LOG`;
      const settingsNamespace = `${companyId}#MAINT_SETTINGS`;

      try {
        const [logsRes, settingsRes] = await Promise.all([
          client.graphql({
            query: MaintenanceByCompanyByStart,
            variables: {
              CompanyId: logNamespace,
              limit: 1000,
            },
            authMode: 'userPool',
          }),
          client.graphql({
            query: MaintenanceByCompanyByStart,
            variables: {
              CompanyId: settingsNamespace,
              limit: 20,
            },
            authMode: 'userPool',
          }),
        ]);

        const rawLogs = (
          logsRes.data?.maintenanceByCompanyByStart?.items || []
        ).filter(Boolean);
        const parsedLogs = [];
        const kmOverrides = {};

        for (const item of rawLogs) {
          const meta = tryParseJson(item.notes) || {};
          if (meta.kind === 'CAR_KM_OVERRIDE') {
            if (item.carId && meta.currentKm != null) {
              kmOverrides[item.carId] = {
                recordId: item.id,
                createdAt: item.createdAt,
                currentKm: Number(meta.currentKm),
                updatedAt: item.updatedAt,
              };
            }
            continue;
          }

          parsedLogs.push({
            id: item.id,
            CompanyId: companyId,
            carId: item.carId,
            serviceType: meta.serviceType || 'OIL_CHANGE',
            title: item.title || meta.title || 'Oil Change',
            serviceDate: meta.serviceDate || toYmd(item.start),
            nextDueDate:
              meta.nextDueDate !== undefined ? meta.nextDueDate : toYmd(item.end),
            serviceKm: meta.serviceKm ?? '',
            currentKm: meta.currentKm ?? '',
            nextDueKm: meta.nextDueKm ?? '',
            cost: meta.cost ?? '',
            currency: meta.currency || defaultCurrency,
            workshop: meta.workshop || '',
            notes: meta.notes || '',
            reminderEnabled: meta.reminderEnabled !== false,
            reminderLeadDays: Number(meta.reminderLeadDays ?? 14),
            reminderLeadKm: Number(meta.reminderLeadKm ?? 500),
            lastReminderSentAt: meta.lastReminderSentAt || null,
            status: meta.status || 'ACTIVE',
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
          });
        }

        setServiceLogs(parsedLogs);
        setManualKmByCarId(kmOverrides);

        // Parse settings
        const rawSettings = (
          settingsRes.data?.maintenanceByCompanyByStart?.items || []
        ).filter(Boolean);
        const defaultEmails = [claims?.email, 'info@hastecarrental.com']
          .filter(Boolean)
          .filter((v, i, a) => a.indexOf(v) === i)
          .join(', ');

        if (rawSettings.length > 0) {
          const latest = rawSettings[0];
          const sMeta = tryParseJson(latest.notes) || {};
          setSettingsRecordId(latest.id);
          setReminderSettings({
            recipientEmails: sMeta.recipientEmails || defaultEmails,
            autoSendEnabled: sMeta.autoSendEnabled !== false,
            defaultLeadDays: Number(sMeta.defaultLeadDays ?? 14),
            defaultLeadKm: Number(sMeta.defaultLeadKm ?? 500),
            greenwayLeadDays: Number(sMeta.greenwayLeadDays ?? 14),
            lastAutoReminderSentAt: sMeta.lastAutoReminderSentAt || null,
          });
        } else {
          setSettingsRecordId(null);
          setReminderSettings((prev) => ({
            ...prev,
            recipientEmails: prev.recipientEmails || defaultEmails,
          }));
        }
      } catch (err) {
        console.error('MaintenancePage: failed to load service logs', err);
        showAlert('Failed to load maintenance logs.', 'error');
      } finally {
        setLogsLoading(false);
      }
    },
    [claims?.email, defaultCurrency, showAlert]
  );

  const loadGreenwayRecords = useCallback(async (geCompanyId) => {
    if (!geCompanyId) return;
    setGreenwayLoading(true);
    const gwNamespace = `${geCompanyId}#GREENWAY`;
    try {
      const res = await rawClient.graphql({
        query: MaintenanceByCompanyByStart,
        variables: {
          CompanyId: gwNamespace,
          limit: 1000,
        },
        authMode: 'userPool',
      });
      const items = (
        res.data?.maintenanceByCompanyByStart?.items || []
      ).filter(Boolean);
      const byCar = {};
      for (const item of items) {
        const meta = tryParseJson(item.notes) || {};
        const prev = byCar[item.carId];
        if (
          prev?.updatedAt &&
          item.updatedAt &&
          new Date(prev.updatedAt).getTime() > new Date(item.updatedAt).getTime()
        ) {
          continue;
        }
        byCar[item.carId] = {
          recordId: item.id,
          carId: item.carId,
          plate: meta.plate || '',
          deadlineDate:
            meta.deadlineDate !== undefined
              ? meta.deadlineDate
              : toYmd(item.end) || '',
          lastInspectionDate: meta.lastInspectionDate || '',
          isResultTypeSuccess:
            meta.isResultTypeSuccess !== undefined ? meta.isResultTypeSuccess : true,
          isExempt: !!meta.isExempt,
          notFoundOnGreenway: !!meta.notFoundOnGreenway,
          statusText: meta.statusText || '',
          source: meta.source || 'manual',
          lastCheckedAt: meta.lastCheckedAt || item.updatedAt || null,
          reminderEnabled: meta.reminderEnabled !== false,
          reminderLeadDays: Number(meta.reminderLeadDays ?? 14),
          lastReminderSentAt: meta.lastReminderSentAt || null,
          notes: meta.notes || '',
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        };
      }
      greenwayByCarIdRef.current = byCar;
      setGreenwayByCarId(byCar);
      setLoadedGreenwayCompanyId(geCompanyId);
    } catch (err) {
      console.error('MaintenancePage: failed to load Greenway records', err);
      setLoadedGreenwayCompanyId(geCompanyId);
    } finally {
      setGreenwayLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ready) return;
    loadCompaniesAndFleet();
  }, [ready, loadCompaniesAndFleet]);

  useEffect(() => {
    if (!ready || !activeCompanyId) return;
    autoReminderCheckedRef.current = false;
    loadHandoverOdometers(activeCompanyId);
    loadServiceLogsAndSettings(activeCompanyId);
  }, [ready, activeCompanyId, loadHandoverOdometers, loadServiceLogsAndSettings]);

  useEffect(() => {
    if (!ready) return;
    const targetGeId = georgiaCompanyId || (isGeorgiaSelected ? activeCompanyId : null);
    if (targetGeId) {
      loadGreenwayRecords(targetGeId);
    }
  }, [ready, georgiaCompanyId, isGeorgiaSelected, activeCompanyId, loadGreenwayRecords]);

  // Keep currency in logForm synced with country
  useEffect(() => {
    setLogForm((prev) => ({ ...prev, currency: defaultCurrency }));
  }, [defaultCurrency]);

  /* ===================== Derived Car Maps & Odometers ===================== */
  const carById = useMemo(() => {
    const map = new Map();
    for (const c of cars) map.set(c.id, c);
    for (const c of georgiaCars) {
      if (!map.has(c.id)) map.set(c.id, c);
    }
    return map;
  }, [cars, georgiaCars]);

  const getCarCurrentKm = useCallback(
    (carId) => {
      if (!carId) return null;
      const manual = manualKmByCarId[carId]?.currentKm;
      const handover = handoverKmByCarId[carId];
      // Also check highest serviceKm or currentKm across logs for this car
      let maxLogKm = 0;
      for (const l of serviceLogs) {
        if (l.carId !== carId) continue;
        const ckm = Number(l.currentKm || 0);
        const skm = Number(l.serviceKm || 0);
        if (ckm > maxLogKm) maxLogKm = ckm;
        if (skm > maxLogKm) maxLogKm = skm;
      }
      const candidates = [manual, handover, maxLogKm].filter(
        (v) => v != null && Number.isFinite(Number(v)) && Number(v) > 0
      );
      return candidates.length ? Math.max(...candidates.map(Number)) : null;
    },
    [manualKmByCarId, handoverKmByCarId, serviceLogs]
  );

  /* ===================== Save Car Odometer Override ===================== */
  async function handleSaveCarOdometer(carId, newKmVal) {
    if (!canEdit || !activeCompanyId || !carId) return;
    const kmNum = Number(newKmVal);
    if (!Number.isFinite(kmNum) || kmNum < 0) {
      showAlert('Please enter a valid odometer reading in km.', 'error');
      return;
    }

    const logNamespace = `${activeCompanyId}#MAINT_LOG`;
    const nowIso = new Date().toISOString();
    const existing = manualKmByCarId[carId];
    const notesJson = JSON.stringify({
      kind: 'CAR_KM_OVERRIDE',
      currentKm: Math.round(kmNum),
      updatedAt: nowIso,
    });

    try {
      if (existing?.recordId) {
        const updateInput = {
          id: existing.recordId,
          CompanyId: logNamespace,
          carId,
          title: 'CAR_KM_OVERRIDE',
          start: nowIso,
          end: nowIso,
          notes: notesJson,
          approvalStatus: 'APPROVED',
        };
        if (existing.createdAt) updateInput.createdAt = existing.createdAt;
        await client.graphql({
          query: UpdateMaintenance,
          variables: { input: updateInput },
          authMode: 'userPool',
        });
      } else {
        const res = await client.graphql({
          query: CreateMaintenance,
          variables: {
            input: {
              CompanyId: logNamespace,
              carId,
              title: 'CAR_KM_OVERRIDE',
              start: nowIso,
              end: nowIso,
              notes: notesJson,
              approvalStatus: 'APPROVED',
            },
          },
          authMode: 'userPool',
        });
        const created = res.data?.createMaintenance;
        setManualKmByCarId((prev) => ({
          ...prev,
          [carId]: {
            recordId: created?.id,
            createdAt: created?.createdAt,
            currentKm: Math.round(kmNum),
          },
        }));
      }

      setManualKmByCarId((prev) => ({
        ...prev,
        [carId]: {
          ...(prev[carId] || {}),
          currentKm: Math.round(kmNum),
        },
      }));
      setEditingKmCarId(null);
      setEditingKmValue('');
      showAlert('Odometer updated.', 'success');
    } catch (err) {
      console.error('Failed to update odometer:', err);
      showAlert('Failed to save odometer reading.', 'error');
    }
  }

  /* ===================== Save Email Reminder Settings ===================== */
  async function handleSaveReminderSettings(customPatch = null) {
    if (!activeCompanyId) return;
    const nextSettings = customPatch
      ? { ...reminderSettings, ...customPatch }
      : reminderSettings;

    const settingsNamespace = `${activeCompanyId}#MAINT_SETTINGS`;
    const nowIso = new Date().toISOString();
    const notesJson = JSON.stringify(nextSettings);

    try {
      if (settingsRecordId) {
        await client.graphql({
          query: UpdateMaintenance,
          variables: {
            input: {
              id: settingsRecordId,
              CompanyId: settingsNamespace,
              carId: 'SETTINGS',
              title: 'MAINT_SETTINGS',
              start: nowIso,
              end: nowIso,
              notes: notesJson,
              approvalStatus: 'APPROVED',
            },
          },
          authMode: 'userPool',
        });
      } else {
        const res = await client.graphql({
          query: CreateMaintenance,
          variables: {
            input: {
              CompanyId: settingsNamespace,
              carId: 'SETTINGS',
              title: 'MAINT_SETTINGS',
              start: nowIso,
              end: nowIso,
              notes: notesJson,
              approvalStatus: 'APPROVED',
            },
          },
          authMode: 'userPool',
        });
        const createdId = res.data?.createMaintenance?.id;
        if (createdId) setSettingsRecordId(createdId);
      }
      setReminderSettings(nextSettings);
      if (!customPatch) {
        showAlert('Reminder settings saved.', 'success');
      }
    } catch (err) {
      console.error('Failed to save reminder settings:', err);
      if (!customPatch) showAlert('Failed to save reminder settings.', 'error');
    }
  }

  /* ===================== Enriched & Sorted Service Logs ===================== */
  const enrichedServiceLogs = useMemo(() => {
    return serviceLogs
      .map((log) => {
        const car = carById.get(log.carId);
        const carKm = getCarCurrentKm(log.carId);
        const evaluation = evaluateServiceLogStatus(
          log,
          carKm,
          reminderSettings.defaultLeadDays,
          reminderSettings.defaultLeadKm
        );
        return {
          ...log,
          car,
          carName: car?.name || 'Unknown Car',
          carPlate: car?.plate || '',
          effectiveCurrentKm: carKm || (log.currentKm ? Number(log.currentKm) : null),
          evaluation,
        };
      })
      .sort((a, b) => a.evaluation.urgencyScore - b.evaluation.urgencyScore);
  }, [
    serviceLogs,
    carById,
    getCarCurrentKm,
    reminderSettings.defaultLeadDays,
    reminderSettings.defaultLeadKm,
  ]);

  const filteredServiceLogs = useMemo(() => {
    return enrichedServiceLogs.filter((item) => {
      if (logFilterCarId !== 'ALL' && item.carId !== logFilterCarId) return false;
      if (logFilterType !== 'ALL' && item.serviceType !== logFilterType) return false;
      if (logFilterStatus !== 'ALL' && item.evaluation.code !== logFilterStatus) return false;
      return true;
    });
  }, [enrichedServiceLogs, logFilterCarId, logFilterType, logFilterStatus]);

  /* ===================== Chronological Greenway Fleet (Georgia) ===================== */
  const chronologicalGreenwayCars = useMemo(() => {
    const sourceCars = georgiaCars.length > 0 ? georgiaCars : isGeorgiaSelected ? cars : [];
    const rows = sourceCars
      .filter((c) => c.active !== false)
      .map((car) => {
        const record = greenwayByCarId[car.id] || null;
        const status = evaluateGreenwayStatus(record, reminderSettings.greenwayLeadDays);
        return {
          car,
          record,
          status,
          cleanPlate: normalizePlate(car.plate),
        };
      });

    // Strict chronological sorting by deadline (earliest / most overdue first)
    rows.sort((a, b) => {
      if (a.status.sortKey !== b.status.sortKey) {
        return a.status.sortKey - b.status.sortKey;
      }
      return (a.car.name || '').localeCompare(b.car.name || '');
    });

    if (!greenwaySearch.trim()) return rows;
    const q = greenwaySearch.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (r.car.name || '').toLowerCase().includes(q) ||
        (r.car.plate || '').toLowerCase().includes(q) ||
        (r.record?.deadlineDate || '').toLowerCase().includes(q)
    );
  }, [
    georgiaCars,
    isGeorgiaSelected,
    cars,
    greenwayByCarId,
    reminderSettings.greenwayLeadDays,
    greenwaySearch,
  ]);

  /* ===================== Send Email Reminders (Manual & Auto) ===================== */
  const sendMaintenanceReminderEmail = useCallback(
    async ({ itemsToRemind, reasonTitle, silent = false }) => {
      if (!itemsToRemind || itemsToRemind.length === 0) {
        if (!silent) showAlert('No items selected for reminder.', 'info');
        return false;
      }

      const recipientsList = String(reminderSettings.recipientEmails || '')
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));

      const primaryEmail =
        recipientsList[0] || claims?.email || 'info@hastecarrental.com';

      const lines = [
        `HASTE CAR RENTAL — MAINTENANCE & GREENWAY CHECK REMINDER`,
        `Country / Fleet: ${effectiveCountryName}`,
        `Alert: ${reasonTitle || 'Scheduled Maintenance & Inspection Check'}`,
        `Recipients: ${recipientsList.join(', ') || primaryEmail}`,
        ``,
        `ITEMS REQUIRING CHECK (${itemsToRemind.length}):`,
        `----------------------------------------`,
      ];

      itemsToRemind.forEach((it, idx) => {
        lines.push(`${idx + 1}. ${it.carName || 'Car'} (${it.plate || 'No plate'})`);
        lines.push(`   Check Type: ${it.type || it.title}`);
        lines.push(`   Status: ${it.statusLabel || it.status}`);
        if (it.dueDate) lines.push(`   Deadline / Due Date: ${it.dueDate}`);
        if (it.kmInfo) lines.push(`   Mileage: ${it.kmInfo}`);
        if (it.checkUrl) lines.push(`   Check Link: ${it.checkUrl}`);
        if (it.notes) lines.push(`   Notes: ${it.notes}`);
        lines.push('');
      });

      lines.push(`Greenway Georgia Inspection Check Portal: https://greenway.ge/en/`);

      const messageText = lines.join('\n');

      setSendingReminder(true);
      try {
        await client.graphql({
          query: SubmitInboundRequestMutation,
          variables: {
            input: {
              type: 'CONTACT_LEAD',
              name: `Haste Maintenance Alert (${effectiveCountryName})`,
              email: primaryEmail,
              country: effectiveCountryName,
              message: messageText,
              source: 'maintenance_reminder',
              payload: JSON.stringify({
                kind: 'MAINTENANCE_REMINDER',
                subject: `[Haste Maintenance] ${reasonTitle || 'Check Reminder'} (${effectiveCountryName})`,
                reason: reasonTitle || 'Maintenance & Greenway Inspection Check',
                country: effectiveCountryName,
                recipients:
                  recipientsList.length > 0 ? recipientsList : [primaryEmail],
                items: itemsToRemind,
              }),
            },
          },
          authMode: 'userPool',
        });

        if (!silent) {
          showAlert(
            `Email reminder sent to ${recipientsList.join(', ') || primaryEmail}!`,
            'success'
          );
        }
        return true;
      } catch (err) {
        console.error('Failed to send reminder email via SES:', err);
        if (!silent) {
          showAlert(
            'Could not send via SES automatically — opening your email client with the reminder pre-filled.',
            'info'
          );
          const subject = encodeURIComponent(
            `[Haste Maintenance] ${reasonTitle || 'Check Reminder'} (${effectiveCountryName})`
          );
          const body = encodeURIComponent(messageText);
          window.open(
            `mailto:${encodeURIComponent(recipientsList.join(',') || primaryEmail)}?subject=${subject}&body=${body}`,
            '_blank'
          );
        }
        return false;
      } finally {
        setSendingReminder(false);
      }
    },
    [reminderSettings.recipientEmails, claims?.email, effectiveCountryName, showAlert]
  );

  // Build list of all currently Due Soon / Overdue items across Service Logs + Greenway
  const allDueReminderPayloadItems = useMemo(() => {
    const out = [];

    for (const log of enrichedServiceLogs) {
      if (
        log.reminderEnabled &&
        (log.evaluation.code === 'OVERDUE' || log.evaluation.code === 'DUE_SOON')
      ) {
        out.push({
          kind: 'SERVICE_LOG',
          logId: log.id,
          carId: log.carId,
          carName: log.carName,
          plate: log.carPlate,
          type: log.title || SERVICE_LABEL_BY_VALUE[log.serviceType] || 'Maintenance',
          statusLabel: `${log.evaluation.label} (${log.evaluation.reasons.join(' · ')})`,
          dueDate: log.nextDueDate ? formatDateShort(log.nextDueDate) : '—',
          kmInfo:
            log.nextDueKm || log.effectiveCurrentKm
              ? `Current: ${log.effectiveCurrentKm ? `${Number(log.effectiveCurrentKm).toLocaleString()} km` : '—'} / Next Due: ${log.nextDueKm ? `${Number(log.nextDueKm).toLocaleString()} km` : '—'}`
              : '',
          checkUrl: `${window.location.origin}/console/maintenance`,
          notes: log.notes || '',
        });
      }
    }

    for (const row of chronologicalGreenwayCars) {
      const { car, record, status, cleanPlate } = row;
      if (
        record?.reminderEnabled !== false &&
        (status.code === 'OVERDUE' || status.code === 'DUE_SOON')
      ) {
        out.push({
          kind: 'GREENWAY',
          carId: car.id,
          carName: car.name,
          plate: car.plate,
          type: 'Greenway Technical Inspection (PTI)',
          statusLabel: `${status.label} (${status.detail})`,
          dueDate: record?.deadlineDate ? formatDateShort(record.deadlineDate) : 'Check required',
          kmInfo: '',
          checkUrl: cleanPlate
            ? `https://greenway.ge/en/booking?vehicleLicensePlate=${cleanPlate}`
            : 'https://greenway.ge/en/',
          notes: record?.notes || 'Check deadline on https://greenway.ge/en/',
        });
      }
    }

    return out;
  }, [enrichedServiceLogs, chronologicalGreenwayCars]);

  // Automatic check & send on load if autoSendEnabled and >24h since last auto-reminder
  useEffect(() => {
    const targetGeId = georgiaCompanyId || (isGeorgiaSelected ? activeCompanyId : null);
    if (
      !ready ||
      !activeCompanyId ||
      logsLoading ||
      greenwayLoading ||
      syncingAllGreenway ||
      (targetGeId && loadedGreenwayCompanyId !== targetGeId) ||
      autoReminderCheckedRef.current
    ) {
      return;
    }
    autoReminderCheckedRef.current = true;

    if (!reminderSettings.autoSendEnabled) return;
    if (allDueReminderPayloadItems.length === 0) return;

    const lastSent = reminderSettings.lastAutoReminderSentAt
      ? new Date(reminderSettings.lastAutoReminderSentAt).getTime()
      : 0;
    const hoursSinceLast = (Date.now() - lastSent) / (1000 * 60 * 60);
    if (hoursSinceLast < 24) return;

    (async () => {
      const ok = await sendMaintenanceReminderEmail({
        itemsToRemind: allDueReminderPayloadItems,
        reasonTitle: `Auto-Reminder: ${allDueReminderPayloadItems.length} Car(s) Due for Maintenance / Greenway Check`,
        silent: true,
      });
      if (ok) {
        const nowIso = new Date().toISOString();
        handleSaveReminderSettings({ lastAutoReminderSentAt: nowIso });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ready,
    activeCompanyId,
    georgiaCompanyId,
    isGeorgiaSelected,
    logsLoading,
    greenwayLoading,
    syncingAllGreenway,
    loadedGreenwayCompanyId,
    reminderSettings.autoSendEnabled,
    reminderSettings.lastAutoReminderSentAt,
    allDueReminderPayloadItems,
    sendMaintenanceReminderEmail,
  ]);

  /* ===================== Service Log Handlers ===================== */
  function resetLogForm(preselectedCarId = '') {
    setLogEditingId(null);
    const carKm = preselectedCarId ? getCarCurrentKm(preselectedCarId) : null;
    setLogForm({
      carId: preselectedCarId || '',
      serviceType: 'OIL_CHANGE',
      customTitle: '',
      serviceDate: todayYmd(),
      nextDueDate: addMonthsToYmd(todayYmd(), 6),
      serviceKm: carKm != null ? String(carKm) : '',
      currentKm: carKm != null ? String(carKm) : '',
      nextDueKm: carKm != null ? String(carKm + 8000) : '',
      cost: '',
      currency: defaultCurrency,
      workshop: '',
      notes: '',
      reminderEnabled: true,
      reminderLeadDays: reminderSettings.defaultLeadDays || 14,
      reminderLeadKm: reminderSettings.defaultLeadKm || 500,
      status: 'ACTIVE',
    });
  }

  function handleSelectPresetForCar(carId, serviceType = 'OIL_CHANGE') {
    const preset =
      SERVICE_PRESETS.find((p) => p.value === serviceType) || SERVICE_PRESETS[0];
    const carKm = getCarCurrentKm(carId);
    const today = todayYmd();
    setLogEditingId(null);
    setLogForm({
      carId,
      serviceType: preset.value,
      customTitle: '',
      serviceDate: today,
      nextDueDate: addMonthsToYmd(today, preset.defaultIntervalMonths || 6),
      serviceKm: carKm != null ? String(carKm) : '',
      currentKm: carKm != null ? String(carKm) : '',
      nextDueKm:
        carKm != null && preset.defaultIntervalKm
          ? String(carKm + preset.defaultIntervalKm)
          : '',
      cost: '',
      currency: defaultCurrency,
      workshop: '',
      notes: '',
      reminderEnabled: true,
      reminderLeadDays: reminderSettings.defaultLeadDays || 14,
      reminderLeadKm: reminderSettings.defaultLeadKm || 500,
      status: 'ACTIVE',
    });
    setActiveTab('logs');
    setTimeout(() => {
      logFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }

  function handleStartEditLog(log) {
    setLogEditingId(log.id);
    setLogForm({
      carId: log.carId || '',
      serviceType: log.serviceType || 'OIL_CHANGE',
      customTitle:
        log.serviceType === 'CUSTOM'
          ? log.title
          : log.title !== SERVICE_LABEL_BY_VALUE[log.serviceType]
            ? log.title
            : '',
      serviceDate: log.serviceDate || todayYmd(),
      nextDueDate: log.nextDueDate || '',
      serviceKm: log.serviceKm != null ? String(log.serviceKm) : '',
      currentKm:
        log.effectiveCurrentKm != null
          ? String(log.effectiveCurrentKm)
          : log.currentKm != null
            ? String(log.currentKm)
            : '',
      nextDueKm: log.nextDueKm != null ? String(log.nextDueKm) : '',
      cost: log.cost != null ? String(log.cost) : '',
      currency: log.currency || defaultCurrency,
      workshop: log.workshop || '',
      notes: log.notes || '',
      reminderEnabled: log.reminderEnabled !== false,
      reminderLeadDays: log.reminderLeadDays ?? 14,
      reminderLeadKm: log.reminderLeadKm ?? 500,
      status: log.status || 'ACTIVE',
    });
    setTimeout(() => {
      logFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }

  async function handleSubmitServiceLog(e) {
    e.preventDefault();
    if (!canEdit) {
      showAlert('You do not have permission to modify maintenance logs.', 'error');
      return;
    }
    if (!activeCompanyId) {
      showAlert('No active company selected.', 'error');
      return;
    }
    if (!logForm.carId) {
      showAlert('Please select a car.', 'error');
      return;
    }

    const presetLabel =
      SERVICE_LABEL_BY_VALUE[logForm.serviceType] || 'Maintenance Service';
    const finalTitle =
      logForm.customTitle?.trim() || presetLabel;

    const serviceDateIso = logForm.serviceDate
      ? new Date(`${logForm.serviceDate}T09:00:00Z`).toISOString()
      : new Date().toISOString();
    const nextDueDateIso = logForm.nextDueDate
      ? new Date(`${logForm.nextDueDate}T09:00:00Z`).toISOString()
      : serviceDateIso;

    const meta = {
      kind: 'SERVICE_LOG',
      serviceType: logForm.serviceType,
      title: finalTitle,
      serviceDate: logForm.serviceDate || todayYmd(),
      nextDueDate: logForm.nextDueDate || '',
      serviceKm: logForm.serviceKm !== '' ? Number(logForm.serviceKm) : null,
      currentKm: logForm.currentKm !== '' ? Number(logForm.currentKm) : null,
      nextDueKm: logForm.nextDueKm !== '' ? Number(logForm.nextDueKm) : null,
      cost: logForm.cost !== '' ? Number(logForm.cost) : null,
      currency: logForm.currency || defaultCurrency,
      workshop: logForm.workshop?.trim() || '',
      notes: logForm.notes?.trim() || '',
      reminderEnabled: !!logForm.reminderEnabled,
      reminderLeadDays: Number(logForm.reminderLeadDays ?? 14),
      reminderLeadKm: Number(logForm.reminderLeadKm ?? 500),
      status: logForm.status || 'ACTIVE',
    };

    const logNamespace = `${activeCompanyId}#MAINT_LOG`;

    try {
      setLogsLoading(true);
      if (logEditingId) {
        const existing = serviceLogs.find((x) => x.id === logEditingId);
        const updateInput = {
          id: logEditingId,
          CompanyId: logNamespace,
          carId: logForm.carId,
          title: finalTitle,
          start: serviceDateIso,
          end: nextDueDateIso,
          notes: JSON.stringify(meta),
          approvalStatus: 'APPROVED',
        };
        if (existing?.createdAt) updateInput.createdAt = existing.createdAt;
        await client.graphql({
          query: UpdateMaintenance,
          variables: { input: updateInput },
          authMode: 'userPool',
        });
        showAlert('Maintenance log updated.', 'success');
      } else {
        await client.graphql({
          query: CreateMaintenance,
          variables: {
            input: {
              CompanyId: logNamespace,
              carId: logForm.carId,
              title: finalTitle,
              start: serviceDateIso,
              end: nextDueDateIso,
              notes: JSON.stringify(meta),
              approvalStatus: 'APPROVED',
            },
          },
          authMode: 'userPool',
        });
        showAlert('Maintenance log saved.', 'success');
      }

      // Also update car odometer if user provided currentKm
      if (logForm.currentKm !== '' && Number.isFinite(Number(logForm.currentKm))) {
        await handleSaveCarOdometer(logForm.carId, Number(logForm.currentKm));
      }

      await loadServiceLogsAndSettings(activeCompanyId);
      resetLogForm();
    } catch (err) {
      console.error('Failed to save service log:', err);
      showAlert('Failed to save maintenance log.', 'error');
    } finally {
      setLogsLoading(false);
    }
  }

  async function handleRenewOrCompleteLog(log) {
    if (!canEdit || !activeCompanyId) return;
    const preset =
      SERVICE_PRESETS.find((p) => p.value === log.serviceType) || SERVICE_PRESETS[0];
    const today = todayYmd();
    const nextDate = addMonthsToYmd(today, preset.defaultIntervalMonths || 6);
    const baseKm = log.effectiveCurrentKm || log.nextDueKm || log.serviceKm || null;
    const nextKm =
      baseKm != null && preset.defaultIntervalKm
        ? Number(baseKm) + preset.defaultIntervalKm
        : null;

    const meta = {
      kind: 'SERVICE_LOG',
      serviceType: log.serviceType,
      title: log.title,
      serviceDate: today,
      nextDueDate: nextDate,
      serviceKm: baseKm,
      currentKm: baseKm,
      nextDueKm: nextKm,
      cost: log.cost || null,
      currency: log.currency || defaultCurrency,
      workshop: log.workshop || '',
      notes: log.notes || '',
      reminderEnabled: log.reminderEnabled !== false,
      reminderLeadDays: log.reminderLeadDays ?? 14,
      reminderLeadKm: log.reminderLeadKm ?? 500,
      status: 'ACTIVE',
    };

    try {
      const updateInput = {
        id: log.id,
        CompanyId: `${activeCompanyId}#MAINT_LOG`,
        carId: log.carId,
        title: log.title,
        start: new Date(`${today}T09:00:00Z`).toISOString(),
        end: new Date(`${nextDate}T09:00:00Z`).toISOString(),
        notes: JSON.stringify(meta),
        approvalStatus: 'APPROVED',
      };
      if (log.createdAt) updateInput.createdAt = log.createdAt;
      await client.graphql({
        query: UpdateMaintenance,
        variables: { input: updateInput },
        authMode: 'userPool',
      });
      await loadServiceLogsAndSettings(activeCompanyId);
      showAlert(
        `Logged service as completed today! Next due: ${formatDateShort(nextDate)}${nextKm ? ` or ${nextKm.toLocaleString()} km` : ''}.`,
        'success'
      );
    } catch (err) {
      console.error('Failed to renew log:', err);
      showAlert('Failed to update log.', 'error');
    }
  }

  async function handleDeleteServiceLog(id) {
    if (!canEdit) return;
    if (!window.confirm('Delete this maintenance log?')) return;
    try {
      await client.graphql({
        query: DeleteMaintenance,
        variables: { input: { id } },
        authMode: 'userPool',
      });
      setServiceLogs((prev) => prev.filter((x) => x.id !== id));
      if (logEditingId === id) resetLogForm();
      showAlert('Maintenance log deleted.', 'success');
    } catch (err) {
      console.error('Failed to delete service log:', err);
      showAlert('Failed to delete maintenance log.', 'error');
    }
  }

  /* ===================== Greenway Handlers ===================== */
  const upsertGreenwayRecord = useCallback(
    async (car, patch, { silentAudit = false } = {}) => {
      const targetGeCompanyId =
        georgiaCompanyId || car.CompanyId || activeCompanyId;
      if (!targetGeCompanyId) return null;

      const gwNamespace = `${targetGeCompanyId}#GREENWAY`;
      const existing = greenwayByCarIdRef.current[car.id] || null;
      const nowIso = new Date().toISOString();

      const merged = {
        kind: 'GREENWAY',
        plate: normalizePlate(car.plate),
        deadlineDate:
          patch.deadlineDate !== undefined
            ? patch.deadlineDate
            : existing?.deadlineDate || '',
        lastInspectionDate:
          patch.lastInspectionDate !== undefined
            ? patch.lastInspectionDate
            : existing?.lastInspectionDate || '',
        isResultTypeSuccess:
          patch.isResultTypeSuccess !== undefined
            ? patch.isResultTypeSuccess
            : existing?.isResultTypeSuccess ?? true,
        isExempt:
          patch.isExempt !== undefined
            ? patch.isExempt
            : existing?.isExempt ?? false,
        notFoundOnGreenway:
          patch.notFoundOnGreenway !== undefined
            ? !!patch.notFoundOnGreenway
            : !!existing?.notFoundOnGreenway,
        statusText:
          patch.statusText !== undefined
            ? patch.statusText
            : existing?.statusText || '',
        source: patch.source || existing?.source || 'manual',
        lastCheckedAt: nowIso,
        reminderEnabled:
          patch.reminderEnabled !== undefined
            ? patch.reminderEnabled
            : existing?.reminderEnabled !== false,
        reminderLeadDays: Number(
          patch.reminderLeadDays ?? existing?.reminderLeadDays ?? 14
        ),
        lastReminderSentAt:
          patch.lastReminderSentAt !== undefined
            ? patch.lastReminderSentAt
            : existing?.lastReminderSentAt || null,
        notes:
          patch.notes !== undefined ? patch.notes : existing?.notes || '',
      };

      // Staff (read-only) users still get live Greenway deadlines in UI state
      if (!canEdit) {
        const memRec = {
          ...merged,
          recordId: existing?.recordId || null,
          carId: car.id,
          createdAt: existing?.createdAt || nowIso,
          updatedAt: nowIso,
        };
        greenwayByCarIdRef.current = {
          ...greenwayByCarIdRef.current,
          [car.id]: memRec,
        };
        setGreenwayByCarId((prev) => ({ ...prev, [car.id]: memRec }));
        return memRec;
      }

      const endIso = merged.deadlineDate
        ? new Date(`${merged.deadlineDate}T09:00:00Z`).toISOString()
        : nowIso;

      const gqlClient = silentAudit ? rawClient : client;

      if (existing?.recordId) {
        const updateInput = {
          id: existing.recordId,
          CompanyId: gwNamespace,
          carId: car.id,
          title: 'Greenway PTI',
          start: nowIso,
          end: endIso,
          notes: JSON.stringify(merged),
          approvalStatus: 'APPROVED',
        };
        if (existing.createdAt) updateInput.createdAt = existing.createdAt;
        const res = await gqlClient.graphql({
          query: UpdateMaintenance,
          variables: { input: updateInput },
          authMode: 'userPool',
        });
        const updated = res.data?.updateMaintenance;
        const nextRec = {
          ...merged,
          recordId: existing.recordId,
          carId: car.id,
          createdAt: updated?.createdAt || existing.createdAt,
          updatedAt: updated?.updatedAt || nowIso,
        };
        greenwayByCarIdRef.current = {
          ...greenwayByCarIdRef.current,
          [car.id]: nextRec,
        };
        setGreenwayByCarId((prev) => ({ ...prev, [car.id]: nextRec }));
        return nextRec;
      } else {
        const res = await gqlClient.graphql({
          query: CreateMaintenance,
          variables: {
            input: {
              CompanyId: gwNamespace,
              carId: car.id,
              title: 'Greenway PTI',
              start: nowIso,
              end: endIso,
              notes: JSON.stringify(merged),
              approvalStatus: 'APPROVED',
            },
          },
          authMode: 'userPool',
        });
        const created = res.data?.createMaintenance;
        const nextRec = {
          ...merged,
          recordId: created?.id,
          carId: car.id,
          createdAt: created?.createdAt,
          updatedAt: created?.updatedAt || nowIso,
        };
        greenwayByCarIdRef.current = {
          ...greenwayByCarIdRef.current,
          [car.id]: nextRec,
        };
        setGreenwayByCarId((prev) => ({ ...prev, [car.id]: nextRec }));
        return nextRec;
      }
    },
    [georgiaCompanyId, activeCompanyId, canEdit]
  );

  const syncCarsFromGreenway = useCallback(
    async (carsToSync, { silent = false } = {}) => {
      const validCars = (carsToSync || []).filter(
        (c) => c && c.active !== false && normalizePlate(c.plate)
      );
      if (validCars.length === 0) {
        if (!silent) {
          showAlert('No active Georgian cars with license plates found.', 'info');
        }
        return { syncedCount: 0, notFoundCount: 0 };
      }

      setSyncingAllGreenway(true);
      let syncedCount = 0;
      let notFoundCount = 0;

      try {
        const targetGeCompanyId =
          georgiaCompanyId || validCars[0]?.CompanyId || activeCompanyId;
        const plates = validCars.map((c) => normalizePlate(c.plate));
        const batchMap = await fetchGreenwayDeadlinesBatch(
          plates,
          targetGeCompanyId
        );

        const SAVE_CONCURRENCY = 5;
        for (let i = 0; i < validCars.length; i += SAVE_CONCURRENCY) {
          const slice = validCars.slice(i, i + SAVE_CONCURRENCY);
          const outcomes = await Promise.all(
            slice.map(async (car) => {
              const normPlate = normalizePlate(car.plate);
              const gwResult = batchMap.get(normPlate);
              if (!gwResult) {
                return 'not_found';
              }

              try {
                if (gwResult.deadlineDate || gwResult.isValid) {
                  await upsertGreenwayRecord(
                    car,
                    {
                      deadlineDate: gwResult.deadlineDate,
                      lastInspectionDate: gwResult.lastInspectionDate,
                      isResultTypeSuccess: gwResult.isResultTypeSuccess,
                      isExempt: gwResult.isExempt,
                      notFoundOnGreenway: false,
                      statusText: gwResult.statusText,
                      source: 'greenway.ge',
                    },
                    { silentAudit: true }
                  );
                  return 'synced';
                }

                const existing = greenwayByCarIdRef.current[car.id];
                if (!existing || existing.source !== 'manual') {
                  await upsertGreenwayRecord(
                    car,
                    {
                      notFoundOnGreenway: true,
                      statusText: gwResult.statusText || 'Not found on Greenway',
                      source: 'greenway.ge',
                    },
                    { silentAudit: true }
                  );
                }
                return 'not_found';
              } catch (saveErr) {
                console.warn('Failed to persist Greenway sync for', car.plate, saveErr);
                return 'not_found';
              }
            })
          );

          for (const o of outcomes) {
            if (o === 'synced') syncedCount++;
            else notFoundCount++;
          }
        }

        if (!silent) {
          showAlert(
            `Greenway sync finished: ${syncedCount} car(s) updated${
              notFoundCount
                ? `, ${notFoundCount} plate(s) not registered on greenway.ge`
                : ''
            }.`,
            syncedCount > 0 ? 'success' : 'info'
          );
        }
      } catch (err) {
        console.warn('Greenway batch sync failed:', err);
        if (!silent) {
          showAlert(err?.message || 'Failed to sync from greenway.ge', 'error');
        }
      } finally {
        setSyncingPlateCarId(null);
        setSyncingAllGreenway(false);
      }

      return { syncedCount, notFoundCount };
    },
    [georgiaCompanyId, activeCompanyId, upsertGreenwayRecord, showAlert]
  );

  // Automatically fetch Greenway deadlines on page load for any unchecked or stale (>12h) Georgian cars
  useEffect(() => {
    const targetGeId =
      georgiaCompanyId || (isGeorgiaSelected ? activeCompanyId : null);
    if (
      !ready ||
      !targetGeId ||
      carsLoading ||
      greenwayLoading ||
      loadedGreenwayCompanyId !== targetGeId
    ) {
      return;
    }

    const sourceCars = (
      georgiaCars.length > 0 ? georgiaCars : isGeorgiaSelected ? cars : []
    ).filter((c) => c.active !== false && normalizePlate(c.plate));

    if (sourceCars.length === 0) return;
    if (autoGreenwaySyncedForCompanyRef.current === targetGeId) return;
    autoGreenwaySyncedForCompanyRef.current = targetGeId;

    const STALE_MS = 12 * 60 * 60 * 1000; // 12 hours
    const nowMs = Date.now();

    const carsNeedingAutoSync = sourceCars.filter((car) => {
      const rec = greenwayByCarIdRef.current[car.id];
      if (!rec) return true;
      if (rec.source === 'manual' && rec.deadlineDate) return false;
      if (!rec.lastCheckedAt) return true;
      const checkedMs = new Date(rec.lastCheckedAt).getTime();
      if (Number.isNaN(checkedMs)) return true;
      return nowMs - checkedMs >= STALE_MS;
    });

    if (carsNeedingAutoSync.length > 0) {
      syncCarsFromGreenway(carsNeedingAutoSync, { silent: true });
    }
  }, [
    ready,
    georgiaCompanyId,
    isGeorgiaSelected,
    activeCompanyId,
    carsLoading,
    greenwayLoading,
    loadedGreenwayCompanyId,
    georgiaCars,
    cars,
    syncCarsFromGreenway,
  ]);

  async function handleSyncSingleCarFromGreenway(car) {
    if (!car?.plate) {
      showAlert(`Car "${car?.name}" does not have a plate number set.`, 'error');
      return;
    }
    setSyncingPlateCarId(car.id);
    try {
      const targetGeCompanyId =
        georgiaCompanyId || car.CompanyId || activeCompanyId;
      const gwResult = await fetchGreenwayDeadlineByPlate(
        car.plate,
        targetGeCompanyId
      );

      if (!gwResult.isValid && !gwResult.deadlineDate) {
        const existing = greenwayByCarIdRef.current[car.id];
        if (!existing || existing.source !== 'manual') {
          await upsertGreenwayRecord(
            car,
            {
              notFoundOnGreenway: true,
              statusText: gwResult.statusText || 'Not found on Greenway',
              source: 'greenway.ge',
            },
            { silentAudit: true }
          );
        }
        showAlert(
          `Plate ${normalizePlate(car.plate)} was not found in Greenway's database. You can set its deadline manually or verify on greenway.ge.`,
          'info'
        );
        return;
      }

      await upsertGreenwayRecord(
        car,
        {
          deadlineDate: gwResult.deadlineDate,
          lastInspectionDate: gwResult.lastInspectionDate,
          isResultTypeSuccess: gwResult.isResultTypeSuccess,
          isExempt: gwResult.isExempt,
          notFoundOnGreenway: false,
          statusText: gwResult.statusText,
          source: 'greenway.ge',
        },
        { silentAudit: true }
      );

      showAlert(
        `${car.name} (${car.plate}): Greenway deadline synced — ${gwResult.deadlineDisplay || gwResult.deadlineDate}`,
        'success'
      );
    } catch (err) {
      console.warn('Greenway sync warning:', err);
      showAlert(err?.message || 'Failed to sync from greenway.ge', 'error');
    } finally {
      setSyncingPlateCarId(null);
    }
  }

  async function handleSyncAllGeorgiaCarsFromGreenway() {
    const sourceCars = (
      georgiaCars.length > 0 ? georgiaCars : isGeorgiaSelected ? cars : []
    ).filter((c) => c.active !== false && c.plate);
    await syncCarsFromGreenway(sourceCars, { silent: false });
  }

  function handleOpenEditGreenwayModal(car) {
    const existing = greenwayByCarId[car.id] || {};
    setEditingGreenwayCar(car);
    setGreenwayForm({
      deadlineDate: existing.deadlineDate || '',
      lastInspectionDate: existing.lastInspectionDate || '',
      isExempt: !!existing.isExempt,
      reminderEnabled: existing.reminderEnabled !== false,
      reminderLeadDays: existing.reminderLeadDays ?? 14,
      notes: existing.notes || '',
    });
  }

  async function handleSaveManualGreenway(e) {
    e.preventDefault();
    if (!editingGreenwayCar) return;
    try {
      await upsertGreenwayRecord(editingGreenwayCar, {
        deadlineDate: greenwayForm.deadlineDate,
        lastInspectionDate: greenwayForm.lastInspectionDate,
        isResultTypeSuccess:
          greenwayForm.deadlineDate
            ? (daysUntilYmd(greenwayForm.deadlineDate) ?? 0) >= 0
            : true,
        isExempt: !!greenwayForm.isExempt,
        notFoundOnGreenway: false,
        statusText: greenwayForm.isExempt
          ? 'Exempt (< 4 years old)'
          : 'Manually verified',
        source: 'manual',
        reminderEnabled: !!greenwayForm.reminderEnabled,
        reminderLeadDays: Number(greenwayForm.reminderLeadDays ?? 14),
        notes: greenwayForm.notes?.trim() || '',
      });
      setEditingGreenwayCar(null);
      showAlert('Greenway inspection deadline saved.', 'success');
    } catch (err) {
      console.error('Failed to save Greenway deadline:', err);
      showAlert('Failed to save Greenway deadline.', 'error');
    }
  }

  /* ===================== Calendar Schedule Handlers (from CarsPage) ===================== */
  const loadCalendarSchedule = useCallback(
    async (companyId, carId) => {
      if (!companyId || !carId) return;
      setScheduleLoading(true);
      try {
        const res = await client.graphql({
          query: ListMaintenances,
          variables: {
            filter: {
              CompanyId: { eq: companyId },
              carId: { eq: carId },
            },
            limit: 500,
          },
          authMode: 'userPool',
        });
        const items = (res?.data?.listMaintenances?.items || []).filter(Boolean);
        items.sort(
          (a, b) =>
            new Date(b.start || 0).getTime() - new Date(a.start || 0).getTime()
        );
        setScheduleItems(items);
      } catch (err) {
        console.error('Failed to load calendar maintenance schedule:', err);
        showAlert('Error loading calendar maintenance schedule.', 'error');
      } finally {
        setScheduleLoading(false);
      }
    },
    [showAlert]
  );

  useEffect(() => {
    if (!activeCompanyId || !scheduleCarId) {
      setScheduleItems([]);
      return;
    }
    loadCalendarSchedule(activeCompanyId, scheduleCarId);
  }, [activeCompanyId, scheduleCarId, loadCalendarSchedule]);

  async function handleSubmitCalendarSchedule(e) {
    e.preventDefault();
    if (!activeCompanyId || !scheduleCarId) {
      showAlert('Select a car first.', 'error');
      return;
    }
    if (!canEdit) {
      showAlert('You are not allowed to modify maintenance records.', 'error');
      return;
    }
    if (!scheduleForm.title?.trim()) {
      showAlert('Title/description is required.', 'error');
      return;
    }
    if (!scheduleForm.start || !scheduleForm.end) {
      showAlert('Start and end are required.', 'error');
      return;
    }

    const startDate = new Date(scheduleForm.start);
    const endDate = new Date(scheduleForm.end);
    if (
      !Number.isFinite(startDate.getTime()) ||
      !Number.isFinite(endDate.getTime()) ||
      endDate <= startDate
    ) {
      showAlert('End time must be after start time.', 'error');
      return;
    }

    const basePayload = {
      CompanyId: activeCompanyId,
      carId: scheduleCarId,
      title: scheduleForm.title.trim(),
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      notes: scheduleForm.notes?.trim() || null,
    };

    try {
      if (isAdmin) {
        if (scheduleEditingId) {
          const existing = scheduleItems.find((m) => m.id === scheduleEditingId);
          const updateInput = {
            id: scheduleEditingId,
            ...basePayload,
            approvalStatus: 'APPROVED',
          };
          if (existing?.createdAt) updateInput.createdAt = existing.createdAt;
          await client.graphql({
            query: UpdateMaintenance,
            variables: { input: updateInput },
            authMode: 'userPool',
          });
          showAlert('Calendar maintenance updated.', 'success');
        } else {
          await client.graphql({
            query: CreateMaintenance,
            variables: {
              input: {
                ...basePayload,
                approvalStatus: 'APPROVED',
              },
            },
            authMode: 'userPool',
          });
          showAlert('Calendar maintenance block created.', 'success');
        }
      } else {
        await client.graphql({
          query: ProposeChange,
          variables: {
            input: {
              CompanyId: activeCompanyId,
              entity: 'MAINTENANCE',
              op: scheduleEditingId ? 'UPDATE' : 'CREATE',
              targetId: scheduleEditingId || null,
              payload: JSON.stringify(basePayload),
            },
          },
          authMode: 'userPool',
        });
        showAlert('Maintenance calendar block sent for admin approval.', 'success');
      }

      await loadCalendarSchedule(activeCompanyId, scheduleCarId);
      setScheduleEditingId(null);
      setScheduleForm({ title: '', start: '', end: '', notes: '' });
    } catch (err) {
      console.error('Failed to save calendar maintenance:', err);
      showAlert('Error saving calendar maintenance.', 'error');
    }
  }

  async function handleDeleteCalendarSchedule(id) {
    if (!isAdmin) {
      showAlert('Only admins can delete calendar maintenance blocks.', 'error');
      return;
    }
    if (!window.confirm('Delete this calendar maintenance block?')) return;
    try {
      await client.graphql({
        query: DeleteMaintenance,
        variables: { input: { id } },
        authMode: 'userPool',
      });
      setScheduleItems((prev) => prev.filter((x) => x.id !== id));
      showAlert('Calendar maintenance record deleted.', 'success');
    } catch (err) {
      console.error('Failed to delete calendar maintenance:', err);
      showAlert('Error deleting calendar maintenance.', 'error');
    }
  }

  /* ===================== Summary Counts ===================== */
  const kpiStats = useMemo(() => {
    const activeFleetCount = cars.filter((c) => c.active !== false).length;
    const logsOverdue = enrichedServiceLogs.filter(
      (l) => l.evaluation.code === 'OVERDUE'
    ).length;
    const logsDueSoon = enrichedServiceLogs.filter(
      (l) => l.evaluation.code === 'DUE_SOON'
    ).length;
    const gwOverdue = chronologicalGreenwayCars.filter(
      (r) => r.status.code === 'OVERDUE'
    ).length;
    const gwDueSoon = chronologicalGreenwayCars.filter(
      (r) => r.status.code === 'DUE_SOON'
    ).length;
    const gwUnchecked = chronologicalGreenwayCars.filter(
      (r) => r.status.code === 'UNCHECKED'
    ).length;

    return {
      activeFleetCount,
      logsOverdue,
      logsDueSoon,
      gwOverdue,
      gwDueSoon,
      gwUnchecked,
      totalAlerts: logsOverdue + logsDueSoon + gwOverdue + gwDueSoon,
    };
  }, [cars, enrichedServiceLogs, chronologicalGreenwayCars]);

  if (!ready || (isAdmin && loadingCompany)) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 14, color: COLORS.gray[500] }}>
          Loading Maintenance & Greenway Tracker…
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: COLORS.gray[50],
        minHeight: 'calc(100vh - 64px)',
        position: 'relative',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: isMobile ? '14px 10px 32px' : '24px 18px 48px',
          display: 'grid',
          gap: 18,
        }}
      >
        {/* ===================== Header ===================== */}
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: isMobile ? 22 : 28,
                  fontWeight: 800,
                  color: COLORS.gray[900],
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <Wrench size={24} color={COLORS.primary} />
                <span>Maintenance & Greenway PTI</span>
              </h3>
              <span
                style={{
                  padding: '4px 12px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 800,
                  background: '#0f172a',
                  color: '#fff',
                }}
              >
                {effectiveCountryName} Fleet
              </span>
            </div>
            <p
              style={{
                margin: '6px 0 0',
                fontSize: 14,
                color: COLORS.gray[600],
                lineHeight: 1.45,
              }}
            >
              Track oil changes, mileage & date intervals, email check reminders, and{' '}
              <a
                href="https://greenway.ge/en/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#166534',
                  fontWeight: 700,
                  textDecoration: 'underline',
                }}
              >
                Greenway Georgia
              </a>{' '}
              inspection deadlines in chronological order.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              type="button"
              onClick={() => setReminderSettingsOpen((v) => !v)}
              style={{
                background: reminderSettingsOpen ? '#eff6ff' : '#ffffff',
                color: COLORS.gray[800],
                border: `1px solid ${COLORS.gray[300]}`,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 700,
              }}
            >
              <Settings size={15} />
              <span>Email Settings</span>
            </Button>

            <Button
              type="button"
              disabled={sendingReminder || allDueReminderPayloadItems.length === 0}
              onClick={() =>
                sendMaintenanceReminderEmail({
                  itemsToRemind: allDueReminderPayloadItems,
                  reasonTitle: `${allDueReminderPayloadItems.length} Car(s) Due for Maintenance / Greenway Check`,
                })
              }
              style={{
                background:
                  allDueReminderPayloadItems.length > 0 ? '#111827' : COLORS.gray[200],
                color: allDueReminderPayloadItems.length > 0 ? '#ffffff' : COLORS.gray[500],
                border: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                fontWeight: 800,
                cursor:
                  allDueReminderPayloadItems.length > 0 ? 'pointer' : 'not-allowed',
              }}
            >
              <Mail size={15} />
              <span>
                {sendingReminder
                  ? 'Sending…'
                  : `Send Check Reminder (${allDueReminderPayloadItems.length})`}
              </span>
            </Button>
          </div>
        </header>

        {/* ===================== KPI Summary Cards ===================== */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 12,
          }}
        >
          <div style={cardStyle({ padding: 16 })}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.gray[500] }}>
              ACTIVE {effectiveCountryName.toUpperCase()} CARS
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 26,
                fontWeight: 900,
                color: COLORS.gray[900],
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
              }}
            >
              <span>{kpiStats.activeFleetCount}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray[500] }}>
                vehicles in fleet
              </span>
            </div>
          </div>

          <div
            style={cardStyle({
              padding: 16,
              borderLeft:
                kpiStats.logsOverdue > 0
                  ? '4px solid #ef4444'
                  : kpiStats.logsDueSoon > 0
                    ? '4px solid #f59e0b'
                    : '4px solid #10b981',
            })}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.gray[500] }}>
              OIL & SERVICE LOG ALERTS
            </div>
            <div
              style={{
                marginTop: 6,
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: 26,
                  fontWeight: 900,
                  color: kpiStats.logsOverdue > 0 ? '#dc2626' : COLORS.gray[900],
                }}
              >
                {kpiStats.logsOverdue} overdue
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>
                · {kpiStats.logsDueSoon} due soon
              </span>
            </div>
          </div>

          <div
            style={cardStyle({
              padding: 16,
              borderLeft:
                kpiStats.gwOverdue > 0
                  ? '4px solid #ef4444'
                  : kpiStats.gwDueSoon > 0
                    ? '4px solid #f59e0b'
                    : '4px solid #16a34a',
            })}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: '#166534' }}>
              GREENWAY GEORGIA PTI
            </div>
            <div
              style={{
                marginTop: 6,
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: 26,
                  fontWeight: 900,
                  color: kpiStats.gwOverdue > 0 ? '#dc2626' : '#14532d',
                }}
              >
                {kpiStats.gwOverdue} overdue
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>
                · {kpiStats.gwDueSoon} due soon
              </span>
            </div>
          </div>

          <div style={cardStyle({ padding: 16 })}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.gray[500] }}>
              EMAIL REMINDER STATUS
            </div>
            <div style={{ marginTop: 6, fontSize: 14, fontWeight: 800, color: COLORS.gray[900] }}>
              {reminderSettings.autoSendEnabled ? '✓ Auto-Reminders Active' : '○ Manual Only'}
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 11.5,
                color: COLORS.gray[500],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={reminderSettings.recipientEmails}
            >
              To: {reminderSettings.recipientEmails || 'info@hastecarrental.com'}
            </div>
          </div>
        </div>

        {/* ===================== Email Reminder Configuration Panel ===================== */}
        {reminderSettingsOpen && (
          <section
            style={cardStyle({
              background: '#f8fafc',
              border: '1px solid #cbd5e1',
            })}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: COLORS.gray[900] }}>
                  Email Reminder Configuration ({effectiveCountryName})
                </h4>
                <p style={{ margin: '3px 0 0', fontSize: 12.5, color: COLORS.gray[600] }}>
                  Configure who receives maintenance & Greenway PTI check alerts and how early reminders trigger.
                </p>
              </div>
              <Button
                type="button"
                onClick={() => setReminderSettingsOpen(false)}
                style={{ background: '#fff' }}
              >
                Close
              </Button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 14,
                alignItems: 'end',
              }}
            >
              <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                <label style={labelStyle}>Recipient Email(s) (comma-separated)</label>
                <input
                  style={inputStyle}
                  value={reminderSettings.recipientEmails}
                  onChange={(e) =>
                    setReminderSettings((s) => ({
                      ...s,
                      recipientEmails: e.target.value,
                    }))
                  }
                  placeholder="info@hastecarrental.com, ops@hastecarrental.com"
                />
              </div>

              <div>
                <label style={labelStyle}>Service Alert Lead (Days)</label>
                <input
                  style={inputStyle}
                  type="number"
                  min="1"
                  max="120"
                  value={reminderSettings.defaultLeadDays}
                  onChange={(e) =>
                    setReminderSettings((s) => ({
                      ...s,
                      defaultLeadDays: Number(e.target.value || 14),
                    }))
                  }
                />
              </div>

              <div>
                <label style={labelStyle}>Service Alert Lead (KM)</label>
                <input
                  style={inputStyle}
                  type="number"
                  min="50"
                  step="50"
                  value={reminderSettings.defaultLeadKm}
                  onChange={(e) =>
                    setReminderSettings((s) => ({
                      ...s,
                      defaultLeadKm: Number(e.target.value || 500),
                    }))
                  }
                />
              </div>

              <div>
                <label style={labelStyle}>Greenway PTI Lead (Days)</label>
                <input
                  style={inputStyle}
                  type="number"
                  min="1"
                  max="90"
                  value={reminderSettings.greenwayLeadDays}
                  onChange={(e) =>
                    setReminderSettings((s) => ({
                      ...s,
                      greenwayLeadDays: Number(e.target.value || 14),
                    }))
                  }
                />
              </div>

              <div>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    paddingBottom: 8,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!reminderSettings.autoSendEnabled}
                    onChange={(e) =>
                      setReminderSettings((s) => ({
                        ...s,
                        autoSendEnabled: e.target.checked,
                      }))
                    }
                  />
                  <span>Auto-send daily check digest when due</span>
                </label>
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div style={{ fontSize: 12, color: COLORS.gray[500] }}>
                {reminderSettings.lastAutoReminderSentAt
                  ? `Last auto-reminder sent: ${formatDateTime(reminderSettings.lastAutoReminderSentAt)}`
                  : 'No auto-reminder sent yet in this cycle.'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="button"
                  onClick={() => handleSaveReminderSettings()}
                  disabled={!canEdit}
                >
                  Save Reminder Settings
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* ===================== Section Tabs ===================== */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            borderBottom: `1px solid ${COLORS.gray[200]}`,
            paddingBottom: 10,
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab('logs')}
            style={{
              padding: '10px 16px',
              borderRadius: 12,
              border:
                activeTab === 'logs'
                  ? '1px solid #111827'
                  : `1px solid ${COLORS.gray[200]}`,
              background: activeTab === 'logs' ? '#111827' : '#ffffff',
              color: activeTab === 'logs' ? '#ffffff' : COLORS.gray[800],
              fontWeight: 800,
              fontSize: 13.5,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Droplets size={16} />
            <span>Service & Oil Change Logs</span>
            {kpiStats.logsOverdue + kpiStats.logsDueSoon > 0 && (
              <span
                style={{
                  padding: '2px 7px',
                  borderRadius: 999,
                  fontSize: 11,
                  background: '#ef4444',
                  color: '#fff',
                }}
              >
                {kpiStats.logsOverdue + kpiStats.logsDueSoon}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('greenway')}
            style={{
              padding: '10px 16px',
              borderRadius: 12,
              border:
                activeTab === 'greenway'
                  ? '1px solid #166534'
                  : `1px solid ${COLORS.gray[200]}`,
              background: activeTab === 'greenway' ? '#166534' : '#ffffff',
              color: activeTab === 'greenway' ? '#ffffff' : '#166534',
              fontWeight: 800,
              fontSize: 13.5,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <ShieldCheck size={16} />
            <span>Greenway Inspection (Georgia)</span>
            {kpiStats.gwOverdue + kpiStats.gwDueSoon > 0 && (
              <span
                style={{
                  padding: '2px 7px',
                  borderRadius: 999,
                  fontSize: 11,
                  background: '#ef4444',
                  color: '#fff',
                }}
              >
                {kpiStats.gwOverdue + kpiStats.gwDueSoon}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('schedule')}
            style={{
              padding: '10px 16px',
              borderRadius: 12,
              border:
                activeTab === 'schedule'
                  ? '1px solid #2563eb'
                  : `1px solid ${COLORS.gray[200]}`,
              background: activeTab === 'schedule' ? '#2563eb' : '#ffffff',
              color: activeTab === 'schedule' ? '#ffffff' : COLORS.gray[800],
              fontWeight: 800,
              fontSize: 13.5,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Calendar size={16} />
            <span>Calendar Maintenance Blocks</span>
          </button>
        </div>

        {/* =====================================================================
            TAB 1: SERVICE & OIL CHANGE LOGS
           ===================================================================== */}
        {activeTab === 'logs' && (
          <div style={{ display: 'grid', gap: 18 }}>
            {/* Per-Car Quick Fleet Status & Odometer Matrix */}
            <section style={cardStyle()}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12,
                  flexWrap: 'wrap',
                  gap: 10,
                }}
              >
                <div>
                  <h4 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: COLORS.gray[900] }}>
                    {effectiveCountryName} Fleet — Quick Oil Change & Odometer Status
                  </h4>
                  <p style={{ margin: '4px 0 0', fontSize: 12.5, color: COLORS.gray[600] }}>
                    View each car&apos;s latest mileage and oil/service status, or click{' '}
                    <b>+ Log Oil Change</b> to set a maintenance log with email reminders.
                  </p>
                </div>
              </div>

              {carsLoading ? (
                <div style={{ fontSize: 13, color: COLORS.gray[500], padding: 12 }}>
                  Loading cars…
                </div>
              ) : cars.length === 0 ? (
                <div style={{ fontSize: 13, color: COLORS.gray[500], padding: 12 }}>
                  No cars found for {effectiveCountryName}.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          borderBottom: `2px solid ${COLORS.gray[200]}`,
                          color: COLORS.gray[600],
                          textAlign: 'left',
                        }}
                      >
                        <th style={{ padding: '8px 10px' }}>Car</th>
                        <th style={{ padding: '8px 10px' }}>Plate</th>
                        <th style={{ padding: '8px 10px' }}>Current Odometer</th>
                        <th style={{ padding: '8px 10px' }}>Latest Oil / Service Log</th>
                        <th style={{ padding: '8px 10px' }}>Next Due</th>
                        <th style={{ padding: '8px 10px' }}>Status</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cars
                        .filter((c) => c.active !== false)
                        .map((car) => {
                          const carKm = getCarCurrentKm(car.id);
                          const carLogs = enrichedServiceLogs.filter(
                            (l) => l.carId === car.id
                          );
                          const mostUrgentLog = carLogs[0] || null;
                          const badge = mostUrgentLog
                            ? statusBadgeStyle(mostUrgentLog.evaluation.code)
                            : statusBadgeStyle('UNCHECKED');

                          return (
                            <tr
                              key={car.id}
                              style={{
                                borderBottom: `1px solid ${COLORS.gray[100]}`,
                              }}
                            >
                              <td style={{ padding: '10px', fontWeight: 700, color: COLORS.gray[900] }}>
                                {car.name}
                              </td>
                              <td style={{ padding: '10px' }}>
                                <span
                                  style={{
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    padding: '3px 8px',
                                    borderRadius: 6,
                                    background: COLORS.gray[100],
                                    border: `1px solid ${COLORS.gray[300]}`,
                                  }}
                                >
                                  {car.plate || '—'}
                                </span>
                              </td>
                              <td style={{ padding: '10px' }}>
                                {editingKmCarId === car.id ? (
                                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <input
                                      type="number"
                                      value={editingKmValue}
                                      onChange={(e) => setEditingKmValue(e.target.value)}
                                      placeholder="e.g. 54000"
                                      style={{ ...inputStyle, width: 110, padding: '5px 8px' }}
                                    />
                                    <Button
                                      type="button"
                                      onClick={() =>
                                        handleSaveCarOdometer(car.id, editingKmValue)
                                      }
                                      style={{ padding: '5px 8px', fontSize: 12 }}
                                    >
                                      Save
                                    </Button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingKmCarId(null)}
                                      style={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        color: COLORS.gray[500],
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span>
                                      {carKm != null
                                        ? `${Number(carKm).toLocaleString()} km`
                                        : 'Not set'}
                                    </span>
                                    {canEdit && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingKmCarId(car.id);
                                          setEditingKmValue(carKm != null ? String(carKm) : '');
                                        }}
                                        style={{
                                          border: `1px solid ${COLORS.gray[300]}`,
                                          background: '#fff',
                                          borderRadius: 6,
                                          padding: '2px 6px',
                                          fontSize: 11,
                                          cursor: 'pointer',
                                          color: COLORS.gray[700],
                                        }}
                                        title="Update current odometer (km)"
                                      >
                                        Update KM
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '10px' }}>
                                {mostUrgentLog ? (
                                  <div>
                                    <div style={{ fontWeight: 700 }}>{mostUrgentLog.title}</div>
                                    <div style={{ fontSize: 11.5, color: COLORS.gray[500] }}>
                                      Logged: {formatDateShort(mostUrgentLog.serviceDate)}
                                      {mostUrgentLog.serviceKm
                                        ? ` @ ${Number(mostUrgentLog.serviceKm).toLocaleString()} km`
                                        : ''}
                                    </div>
                                  </div>
                                ) : (
                                  <span style={{ color: COLORS.gray[400] }}>No logs yet</span>
                                )}
                              </td>
                              <td style={{ padding: '10px' }}>
                                {mostUrgentLog ? (
                                  <div>
                                    {mostUrgentLog.nextDueDate && (
                                      <div>{formatDateShort(mostUrgentLog.nextDueDate)}</div>
                                    )}
                                    {mostUrgentLog.nextDueKm && (
                                      <div style={{ fontSize: 11.5, color: COLORS.gray[600] }}>
                                        or {Number(mostUrgentLog.nextDueKm).toLocaleString()} km
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td style={{ padding: '10px' }}>
                                {mostUrgentLog ? (
                                  <span
                                    style={{
                                      ...badge,
                                      display: 'inline-block',
                                      padding: '3px 9px',
                                      borderRadius: 999,
                                      fontSize: 11.5,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {mostUrgentLog.evaluation.label} ·{' '}
                                    {mostUrgentLog.evaluation.reasons[0] || ''}
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      ...badge,
                                      display: 'inline-block',
                                      padding: '3px 9px',
                                      borderRadius: 999,
                                      fontSize: 11.5,
                                      fontWeight: 600,
                                    }}
                                  >
                                    No schedule
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '10px', textAlign: 'right' }}>
                                <div
                                  style={{
                                    display: 'inline-flex',
                                    gap: 6,
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-end',
                                  }}
                                >
                                  {canEdit && (
                                    <Button
                                      type="button"
                                      onClick={() =>
                                        handleSelectPresetForCar(car.id, 'OIL_CHANGE')
                                      }
                                      style={{
                                        padding: '5px 10px',
                                        fontSize: 12,
                                        background: COLORS.primaryLight,
                                        color: COLORS.primaryDark,
                                        border: '1px solid #bfdbfe',
                                      }}
                                    >
                                      + Log Oil Change
                                    </Button>
                                  )}
                                  {mostUrgentLog && (
                                    <Button
                                      type="button"
                                      onClick={() =>
                                        sendMaintenanceReminderEmail({
                                          itemsToRemind: [
                                            {
                                              carName: car.name,
                                              plate: car.plate,
                                              type: mostUrgentLog.title,
                                              statusLabel: `${mostUrgentLog.evaluation.label} (${mostUrgentLog.evaluation.reasons.join(', ')})`,
                                              dueDate: formatDateShort(
                                                mostUrgentLog.nextDueDate
                                              ),
                                              kmInfo: mostUrgentLog.nextDueKm
                                                ? `Current: ${carKm || '—'} km / Due: ${mostUrgentLog.nextDueKm} km`
                                                : '',
                                              checkUrl: `${window.location.origin}/console/maintenance`,
                                              notes: mostUrgentLog.notes || '',
                                            },
                                          ],
                                          reasonTitle: `Maintenance Check for ${car.name} (${car.plate})`,
                                        })
                                      }
                                      style={{
                                        padding: '5px 9px',
                                        fontSize: 12,
                                        background: '#fff',
                                      }}
                                      title="Send email reminder to check this car"
                                    >
                                      <Mail size={13} />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Add / Edit Service Log Form */}
            {canEdit && (
              <section ref={logFormRef} style={cardStyle()}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 14,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <div>
                    <h4 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: COLORS.gray[900] }}>
                      {logEditingId ? 'Edit Service / Maintenance Log' : 'Add Service / Oil Change Log'}
                    </h4>
                    <p style={{ margin: '3px 0 0', fontSize: 12.5, color: COLORS.gray[600] }}>
                      Set date and/or mileage intervals with automatic email reminders when a check is due.
                    </p>
                  </div>
                  {logEditingId && (
                    <Button
                      type="button"
                      onClick={() => resetLogForm()}
                      style={{ background: '#fff' }}
                    >
                      Cancel Edit
                    </Button>
                  )}
                </div>

                <form onSubmit={handleSubmitServiceLog} style={{ display: 'grid', gap: 14 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 12,
                    }}
                  >
                    <div>
                      <label style={labelStyle}>Car *</label>
                      <select
                        style={selectStyle}
                        value={logForm.carId}
                        onChange={(e) => {
                          const cid = e.target.value;
                          const ckm = getCarCurrentKm(cid);
                          const preset =
                            SERVICE_PRESETS.find((p) => p.value === logForm.serviceType) ||
                            SERVICE_PRESETS[0];
                          setLogForm((f) => ({
                            ...f,
                            carId: cid,
                            serviceKm: ckm != null ? String(ckm) : f.serviceKm,
                            currentKm: ckm != null ? String(ckm) : f.currentKm,
                            nextDueKm:
                              ckm != null && preset.defaultIntervalKm
                                ? String(ckm + preset.defaultIntervalKm)
                                : f.nextDueKm,
                          }));
                        }}
                        required
                      >
                        <option value="">Select car…</option>
                        {cars.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} ({c.plate || 'No plate'})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={labelStyle}>Maintenance Type *</label>
                      <select
                        style={selectStyle}
                        value={logForm.serviceType}
                        onChange={(e) => {
                          const st = e.target.value;
                          const preset =
                            SERVICE_PRESETS.find((p) => p.value === st) ||
                            SERVICE_PRESETS[0];
                          const baseKm = Number(logForm.serviceKm || logForm.currentKm || 0);
                          setLogForm((f) => ({
                            ...f,
                            serviceType: st,
                            nextDueDate: addMonthsToYmd(
                              f.serviceDate || todayYmd(),
                              preset.defaultIntervalMonths || 6
                            ),
                            nextDueKm:
                              baseKm > 0 && preset.defaultIntervalKm
                                ? String(baseKm + preset.defaultIntervalKm)
                                : f.nextDueKm,
                          }));
                        }}
                      >
                        {SERVICE_PRESETS.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={labelStyle}>Custom Title (optional)</label>
                      <input
                        style={inputStyle}
                        value={logForm.customTitle}
                        onChange={(e) =>
                          setLogForm((f) => ({ ...f, customTitle: e.target.value }))
                        }
                        placeholder={
                          SERVICE_LABEL_BY_VALUE[logForm.serviceType] ||
                          'e.g. 5W-30 Synthetic Oil Change'
                        }
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Date Performed</label>
                      <input
                        style={inputStyle}
                        type="date"
                        value={logForm.serviceDate}
                        onChange={(e) =>
                          setLogForm((f) => ({ ...f, serviceDate: e.target.value }))
                        }
                      />
                    </div>

                    <div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 5,
                        }}
                      >
                        <label style={{ ...labelStyle, marginBottom: 0 }}>
                          Next Due Date (Check Deadline)
                        </label>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {[3, 6, 12].map((m) => (
                            <button
                              key={m}
                              type="button"
                              onClick={() =>
                                setLogForm((f) => ({
                                  ...f,
                                  nextDueDate: addMonthsToYmd(
                                    f.serviceDate || todayYmd(),
                                    m
                                  ),
                                }))
                              }
                              style={{
                                border: `1px solid ${COLORS.gray[300]}`,
                                background: COLORS.gray[50],
                                borderRadius: 5,
                                fontSize: 10.5,
                                padding: '1px 5px',
                                cursor: 'pointer',
                                fontWeight: 700,
                              }}
                            >
                              +{m}m
                            </button>
                          ))}
                        </div>
                      </div>
                      <input
                        style={inputStyle}
                        type="date"
                        value={logForm.nextDueDate}
                        onChange={(e) =>
                          setLogForm((f) => ({ ...f, nextDueDate: e.target.value }))
                        }
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Odometer at Service (km)</label>
                      <input
                        style={inputStyle}
                        type="number"
                        min="0"
                        value={logForm.serviceKm}
                        onChange={(e) => {
                          const val = e.target.value;
                          const preset =
                            SERVICE_PRESETS.find((p) => p.value === logForm.serviceType) ||
                            SERVICE_PRESETS[0];
                          setLogForm((f) => ({
                            ...f,
                            serviceKm: val,
                            currentKm: f.currentKm || val,
                            nextDueKm:
                              val && preset.defaultIntervalKm
                                ? String(Number(val) + preset.defaultIntervalKm)
                                : f.nextDueKm,
                          }));
                        }}
                        placeholder="e.g. 62000"
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Current Car Odometer (km)</label>
                      <input
                        style={inputStyle}
                        type="number"
                        min="0"
                        value={logForm.currentKm}
                        onChange={(e) =>
                          setLogForm((f) => ({ ...f, currentKm: e.target.value }))
                        }
                        placeholder="e.g. 68500"
                      />
                    </div>

                    <div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 5,
                        }}
                      >
                        <label style={{ ...labelStyle, marginBottom: 0 }}>
                          Next Due Odometer (km)
                        </label>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {[8000, 10000, 15000].map((kmAdd) => (
                            <button
                              key={kmAdd}
                              type="button"
                              onClick={() => {
                                const base = Number(
                                  logForm.serviceKm || logForm.currentKm || 0
                                );
                                setLogForm((f) => ({
                                  ...f,
                                  nextDueKm: String(base + kmAdd),
                                }));
                              }}
                              style={{
                                border: `1px solid ${COLORS.gray[300]}`,
                                background: COLORS.gray[50],
                                borderRadius: 5,
                                fontSize: 10.5,
                                padding: '1px 5px',
                                cursor: 'pointer',
                                fontWeight: 700,
                              }}
                            >
                              +{kmAdd / 1000}k
                            </button>
                          ))}
                        </div>
                      </div>
                      <input
                        style={inputStyle}
                        type="number"
                        min="0"
                        value={logForm.nextDueKm}
                        onChange={(e) =>
                          setLogForm((f) => ({ ...f, nextDueKm: e.target.value }))
                        }
                        placeholder="e.g. 70000"
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Cost ({logForm.currency})</label>
                      <input
                        style={inputStyle}
                        type="number"
                        step="0.01"
                        min="0"
                        value={logForm.cost}
                        onChange={(e) =>
                          setLogForm((f) => ({ ...f, cost: e.target.value }))
                        }
                        placeholder="e.g. 65"
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Workshop / Mechanic</label>
                      <input
                        style={inputStyle}
                        value={logForm.workshop}
                        onChange={(e) =>
                          setLogForm((f) => ({ ...f, workshop: e.target.value }))
                        }
                        placeholder="e.g. Tegeta Motors / Bosch Service"
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Notes</label>
                      <input
                        style={inputStyle}
                        value={logForm.notes}
                        onChange={(e) =>
                          setLogForm((f) => ({ ...f, notes: e.target.value }))
                        }
                        placeholder="Oil viscosity, filter brand, remarks…"
                      />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', paddingTop: 18 }}>
                      <label
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!logForm.reminderEnabled}
                          onChange={(e) =>
                            setLogForm((f) => ({
                              ...f,
                              reminderEnabled: e.target.checked,
                            }))
                          }
                        />
                        <span>Send Email Reminder when due</span>
                      </label>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <Button type="submit" disabled={logsLoading}>
                      {logEditingId ? 'Save Log Changes' : 'Add Maintenance Log'}
                    </Button>
                  </div>
                </form>
              </section>
            )}

            {/* Service Logs List (Chronological by Urgency) */}
            <section style={cardStyle()}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 14,
                  flexWrap: 'wrap',
                  gap: 10,
                }}
              >
                <div>
                  <h4 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: COLORS.gray[900] }}>
                    Maintenance & Service Logs ({filteredServiceLogs.length})
                  </h4>
                  <p style={{ margin: '3px 0 0', fontSize: 12.5, color: COLORS.gray[600] }}>
                    Ordered chronologically by soonest due date and remaining mileage.
                  </p>
                </div>

                {/* Filters */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <select
                    style={{ ...selectStyle, width: 'auto', minWidth: 150 }}
                    value={logFilterCarId}
                    onChange={(e) => setLogFilterCarId(e.target.value)}
                  >
                    <option value="ALL">All cars</option>
                    {cars.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.plate})
                      </option>
                    ))}
                  </select>

                  <select
                    style={{ ...selectStyle, width: 'auto', minWidth: 150 }}
                    value={logFilterType}
                    onChange={(e) => setLogFilterType(e.target.value)}
                  >
                    <option value="ALL">All service types</option>
                    {SERVICE_PRESETS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>

                  <select
                    style={{ ...selectStyle, width: 'auto', minWidth: 140 }}
                    value={logFilterStatus}
                    onChange={(e) => setLogFilterStatus(e.target.value)}
                  >
                    <option value="ALL">All statuses</option>
                    <option value="OVERDUE">Overdue</option>
                    <option value="DUE_SOON">Due Soon</option>
                    <option value="UPCOMING">Scheduled / OK</option>
                  </select>
                </div>
              </div>

              {filteredServiceLogs.length === 0 ? (
                <div
                  style={{
                    padding: '24px 16px',
                    textAlign: 'center',
                    color: COLORS.gray[500],
                    fontSize: 13.5,
                  }}
                >
                  No maintenance logs match the current filters. Use the form above or click{' '}
                  <b>+ Log Oil Change</b> on any car row to start tracking!
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {filteredServiceLogs.map((log) => {
                    const badge = statusBadgeStyle(log.evaluation.code);
                    return (
                      <div
                        key={log.id}
                        style={{
                          border: `1px solid ${
                            log.evaluation.code === 'OVERDUE'
                              ? '#fca5a5'
                              : log.evaluation.code === 'DUE_SOON'
                                ? '#fcd34d'
                                : COLORS.gray[200]
                          }`,
                          borderRadius: 12,
                          padding: 14,
                          background:
                            log.evaluation.code === 'OVERDUE'
                              ? '#fff5f5'
                              : log.evaluation.code === 'DUE_SOON'
                                ? '#fffbeb'
                                : '#ffffff',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 12,
                          flexWrap: 'wrap',
                        }}
                      >
                        <div style={{ minWidth: 240, flex: 1 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              flexWrap: 'wrap',
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 800,
                                fontSize: 15,
                                color: COLORS.gray[900],
                              }}
                            >
                              {log.carName} ({log.carPlate || '—'})
                            </span>
                            <span
                              style={{
                                padding: '3px 9px',
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                background: '#eff6ff',
                                color: '#1e40af',
                              }}
                            >
                              {log.title}
                            </span>
                            <span
                              style={{
                                ...badge,
                                padding: '3px 9px',
                                borderRadius: 999,
                                fontSize: 11.5,
                                fontWeight: 800,
                              }}
                            >
                              {log.evaluation.label} — {log.evaluation.reasons.join(' · ')}
                            </span>
                          </div>

                          <div
                            style={{
                              marginTop: 7,
                              display: 'flex',
                              gap: 16,
                              flexWrap: 'wrap',
                              fontSize: 12.5,
                              color: COLORS.gray[700],
                            }}
                          >
                            <span>
                              <b>Last Done:</b> {formatDateShort(log.serviceDate)}
                              {log.serviceKm
                                ? ` (${Number(log.serviceKm).toLocaleString()} km)`
                                : ''}
                            </span>
                            <span>
                              <b>Next Due Date:</b> {formatDateShort(log.nextDueDate)}
                            </span>
                            {log.nextDueKm && (
                              <span>
                                <b>Next Due KM:</b>{' '}
                                {Number(log.nextDueKm).toLocaleString()} km (Current:{' '}
                                {log.effectiveCurrentKm
                                  ? `${Number(log.effectiveCurrentKm).toLocaleString()} km`
                                  : '—'}
                                )
                              </span>
                            )}
                            {log.cost != null && log.cost !== '' && (
                              <span>
                                <b>Cost:</b> {log.cost} {log.currency || defaultCurrency}
                              </span>
                            )}
                            {log.workshop && (
                              <span>
                                <b>Workshop:</b> {log.workshop}
                              </span>
                            )}
                          </div>

                          {log.notes && (
                            <div
                              style={{
                                marginTop: 5,
                                fontSize: 12,
                                color: COLORS.gray[600],
                              }}
                            >
                              Note: {log.notes}
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <Button
                            type="button"
                            onClick={() =>
                              sendMaintenanceReminderEmail({
                                itemsToRemind: [
                                  {
                                    carName: log.carName,
                                    plate: log.carPlate,
                                    type: log.title,
                                    statusLabel: `${log.evaluation.label} (${log.evaluation.reasons.join(', ')})`,
                                    dueDate: formatDateShort(log.nextDueDate),
                                    kmInfo: log.nextDueKm
                                      ? `Current: ${log.effectiveCurrentKm || '—'} km / Next Due: ${log.nextDueKm} km`
                                      : '',
                                    checkUrl: `${window.location.origin}/console/maintenance`,
                                    notes: log.notes || '',
                                  },
                                ],
                                reasonTitle: `${log.title} Check for ${log.carName} (${log.carPlate})`,
                              })
                            }
                            style={{
                              padding: '6px 10px',
                              fontSize: 12,
                              background: '#fff',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 5,
                            }}
                            title="Send Email Reminder to check this car"
                          >
                            <Mail size={13} />
                            <span>Remind</span>
                          </Button>

                          {canEdit && (
                            <>
                              <Button
                                type="button"
                                onClick={() => handleRenewOrCompleteLog(log)}
                                style={{
                                  padding: '6px 10px',
                                  fontSize: 12,
                                  background: '#dcfce7',
                                  color: '#166534',
                                  border: '1px solid #86efac',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 5,
                                }}
                                title="Mark done today and roll next due date/km forward"
                              >
                                <CheckCircle2 size={13} />
                                <span>Done / Renew</span>
                              </Button>

                              <Button
                                type="button"
                                onClick={() => handleStartEditLog(log)}
                                style={{
                                  padding: '6px 8px',
                                  background: '#fff',
                                }}
                                title="Edit log"
                              >
                                <Pencil size={13} />
                              </Button>

                              <DangerButton
                                type="button"
                                onClick={() => handleDeleteServiceLog(log.id)}
                                style={{
                                  padding: '6px 8px',
                                }}
                                title="Delete log"
                              >
                                <Trash2 size={13} />
                              </DangerButton>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}

        {/* =====================================================================
            TAB 2: GREENWAY GEORGIA SECTION (CHRONOLOGICAL ORDER)
           ===================================================================== */}
        {activeTab === 'greenway' && (
          <div style={{ display: 'grid', gap: 18 }}>
            <section
              style={cardStyle({
                background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 65%)',
                border: '1px solid #86efac',
              })}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 14,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ maxWidth: 680 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: 999,
                        background: '#166534',
                        color: '#ffffff',
                        fontSize: 11.5,
                        fontWeight: 800,
                        letterSpacing: '0.04em',
                      }}
                    >
                      GEORGIA PTI TRACKER
                    </span>
                    <h4
                      style={{
                        margin: 0,
                        fontSize: 19,
                        fontWeight: 900,
                        color: '#14532d',
                      }}
                    >
                      Greenway Technical Inspection Deadlines (Chronological Order)
                    </h4>
                  </div>
                  <p
                    style={{
                      margin: '6px 0 0',
                      fontSize: 13,
                      color: COLORS.gray[700],
                      lineHeight: 1.5,
                    }}
                  >
                    Georgian cars are automatically ordered chronologically by their Periodic
                    Technical Inspection (PTI) deadline (most urgent / overdue first). Sync
                    deadlines directly from{' '}
                    <a
                      href="https://greenway.ge/en/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#15803d', fontWeight: 800 }}
                    >
                      https://greenway.ge/en/
                    </a>{' '}
                    or set them manually and send email reminders to check.
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <a
                    href="https://greenway.ge/en/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '9px 14px',
                      borderRadius: 10,
                      border: '1px solid #166534',
                      background: '#ffffff',
                      color: '#166534',
                      fontWeight: 800,
                      fontSize: 13,
                      textDecoration: 'none',
                    }}
                  >
                    <span>Open Greenway.ge</span>
                    <ExternalLink size={14} />
                  </a>

                  {canEdit && (
                    <Button
                      type="button"
                      disabled={syncingAllGreenway}
                      onClick={handleSyncAllGeorgiaCarsFromGreenway}
                      style={{
                        background: '#166534',
                        color: '#ffffff',
                        border: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontWeight: 800,
                      }}
                    >
                      <RefreshCw size={14} />
                      <span>
                        {syncingAllGreenway
                          ? 'Syncing Fleet from Greenway…'
                          : 'Sync All Georgian Cars from Greenway'}
                      </span>
                    </Button>
                  )}

                  <Button
                    type="button"
                    disabled={sendingReminder || chronologicalGreenwayCars.length === 0}
                    onClick={() => {
                      const dueRows = chronologicalGreenwayCars.filter(
                        (r) =>
                          r.status.code === 'OVERDUE' ||
                          r.status.code === 'DUE_SOON' ||
                          r.status.code === 'UNCHECKED'
                      );
                      const targetRows =
                        dueRows.length > 0
                          ? dueRows
                          : chronologicalGreenwayCars.slice(0, 10);

                      sendMaintenanceReminderEmail({
                        itemsToRemind: targetRows.map((r) => ({
                          carName: r.car.name,
                          plate: r.car.plate,
                          type: 'Greenway Georgia PTI Deadline Check',
                          statusLabel: `${r.status.label} (${r.status.detail})`,
                          dueDate: r.record?.deadlineDate
                            ? formatDateShort(r.record.deadlineDate)
                            : 'Check on greenway.ge',
                          checkUrl: r.cleanPlate
                            ? `https://greenway.ge/en/booking?vehicleLicensePlate=${r.cleanPlate}`
                            : 'https://greenway.ge/en/',
                          notes: r.record?.notes || '',
                        })),
                        reasonTitle: 'Greenway Georgia PTI Inspection Check Reminder',
                      });
                    }}
                    style={{
                      background: '#111827',
                      color: '#ffffff',
                      border: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontWeight: 800,
                    }}
                  >
                    <Mail size={14} />
                    <span>Send Greenway Reminder Email</span>
                  </Button>
                </div>
              </div>

              {/* Search bar */}
              <div
                style={{
                  marginTop: 14,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <input
                  style={{ ...inputStyle, maxWidth: 320 }}
                  value={greenwaySearch}
                  onChange={(e) => setGreenwaySearch(e.target.value)}
                  placeholder="Search Georgian car by name or plate…"
                />
                <div style={{ fontSize: 12.5, color: COLORS.gray[600], fontWeight: 600 }}>
                  Showing {chronologicalGreenwayCars.length} Georgian car
                  {chronologicalGreenwayCars.length === 1 ? '' : 's'} sorted chronologically
                </div>
              </div>
            </section>

            {/* Manual Edit Modal / Inline Card for Greenway */}
            {editingGreenwayCar && (
              <section
                style={cardStyle({
                  border: '2px solid #166534',
                  background: '#f0fdf4',
                })}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                  }}
                >
                  <h4 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#14532d' }}>
                    Set Greenway Inspection Deadline — {editingGreenwayCar.name} (
                    {editingGreenwayCar.plate})
                  </h4>
                  <Button
                    type="button"
                    onClick={() => setEditingGreenwayCar(null)}
                    style={{ background: '#fff' }}
                  >
                    Cancel
                  </Button>
                </div>

                <form
                  onSubmit={handleSaveManualGreenway}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 12,
                    alignItems: 'end',
                  }}
                >
                  <div>
                    <label style={labelStyle}>Next Inspection Deadline *</label>
                    <input
                      style={inputStyle}
                      type="date"
                      value={greenwayForm.deadlineDate}
                      onChange={(e) =>
                        setGreenwayForm((f) => ({ ...f, deadlineDate: e.target.value }))
                      }
                      required={!greenwayForm.isExempt}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Last Inspection Date (optional)</label>
                    <input
                      style={inputStyle}
                      type="date"
                      value={greenwayForm.lastInspectionDate}
                      onChange={(e) =>
                        setGreenwayForm((f) => ({
                          ...f,
                          lastInspectionDate: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Reminder Lead Days</label>
                    <input
                      style={inputStyle}
                      type="number"
                      min="1"
                      max="90"
                      value={greenwayForm.reminderLeadDays}
                      onChange={(e) =>
                        setGreenwayForm((f) => ({
                          ...f,
                          reminderLeadDays: Number(e.target.value || 14),
                        }))
                      }
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Notes</label>
                    <input
                      style={inputStyle}
                      value={greenwayForm.notes}
                      onChange={(e) =>
                        setGreenwayForm((f) => ({ ...f, notes: e.target.value }))
                      }
                      placeholder="Service center, gas cert, etc."
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12.5,
                        fontWeight: 700,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!greenwayForm.isExempt}
                        onChange={(e) =>
                          setGreenwayForm((f) => ({ ...f, isExempt: e.target.checked }))
                        }
                      />
                      <span>Car under 4 years old (Exempt)</span>
                    </label>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12.5,
                        fontWeight: 700,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!greenwayForm.reminderEnabled}
                        onChange={(e) =>
                          setGreenwayForm((f) => ({
                            ...f,
                            reminderEnabled: e.target.checked,
                          }))
                        }
                      />
                      <span>Enable Email Reminder</span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      style={{ background: '#166534', color: '#fff', border: 'none' }}
                    >
                      Save Deadline
                    </Button>
                  </div>
                </form>
              </section>
            )}

            {/* Chronological Table of Georgian Cars */}
            <section style={cardStyle()}>
              {chronologicalGreenwayCars.length === 0 ? (
                <div
                  style={{
                    padding: 24,
                    textAlign: 'center',
                    color: COLORS.gray[500],
                    fontSize: 14,
                  }}
                >
                  No Georgian cars found.{' '}
                  {isAdmin && !isGeorgiaSelected && (
                    <button
                      type="button"
                      onClick={() => setCountry('Georgia')}
                      style={{
                        marginLeft: 8,
                        border: 'none',
                        background: '#166534',
                        color: '#fff',
                        padding: '6px 12px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontWeight: 700,
                      }}
                    >
                      Switch to Georgia Fleet
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          borderBottom: `2px solid ${COLORS.gray[200]}`,
                          color: COLORS.gray[600],
                          textAlign: 'left',
                        }}
                      >
                        <th style={{ padding: '10px 8px', width: 48 }}>#</th>
                        <th style={{ padding: '10px 8px' }}>Georgian Car</th>
                        <th style={{ padding: '10px 8px' }}>License Plate</th>
                        <th style={{ padding: '10px 8px' }}>Inspection Deadline</th>
                        <th style={{ padding: '10px 8px' }}>Countdown / Status</th>
                        <th style={{ padding: '10px 8px' }}>Last Passed / Source</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right' }}>
                          Greenway & Reminder Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {chronologicalGreenwayCars.map((row, index) => {
                        const { car, record, status, cleanPlate } = row;
                        const badge = statusBadgeStyle(status.code);
                        const isSyncing = syncingPlateCarId === car.id;

                        return (
                          <tr
                            key={car.id}
                            style={{
                              borderBottom: `1px solid ${COLORS.gray[100]}`,
                              background:
                                status.code === 'OVERDUE'
                                  ? 'rgba(254, 226, 226, 0.35)'
                                  : status.code === 'DUE_SOON'
                                    ? 'rgba(254, 243, 199, 0.35)'
                                    : 'transparent',
                            }}
                          >
                            <td
                              style={{
                                padding: '12px 8px',
                                fontWeight: 800,
                                color: COLORS.gray[500],
                              }}
                            >
                              {index + 1}
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <div
                                style={{
                                  fontWeight: 800,
                                  color: COLORS.gray[900],
                                  fontSize: 14,
                                }}
                              >
                                {car.name}
                              </div>
                              <div style={{ fontSize: 11.5, color: COLORS.gray[500] }}>
                                {[car.year, car.fuelType, car.transmission]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </div>
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span
                                  style={{
                                    fontFamily: 'monospace',
                                    fontWeight: 800,
                                    fontSize: 13.5,
                                    padding: '4px 9px',
                                    borderRadius: 8,
                                    background: '#f8fafc',
                                    border: '1px solid #cbd5e1',
                                    color: '#0f172a',
                                  }}
                                >
                                  {car.plate || '—'}
                                </span>
                                {cleanPlate && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard?.writeText(cleanPlate);
                                      showAlert(
                                        `Copied plate ${cleanPlate} to clipboard.`,
                                        'info'
                                      );
                                    }}
                                    style={{
                                      border: 'none',
                                      background: 'transparent',
                                      cursor: 'pointer',
                                      color: COLORS.gray[500],
                                      padding: 3,
                                    }}
                                    title="Copy normalized plate for greenway.ge"
                                  >
                                    <Copy size={13} />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              {record?.deadlineDate ? (
                                <span
                                  style={{
                                    fontWeight: 800,
                                    fontSize: 14,
                                    color:
                                      status.code === 'OVERDUE'
                                        ? '#dc2626'
                                        : COLORS.gray[900],
                                  }}
                                >
                                  {formatDateShort(record.deadlineDate)}
                                </span>
                              ) : (
                                <span style={{ color: COLORS.gray[400], fontStyle: 'italic' }}>
                                  Not set
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <span
                                style={{
                                  ...badge,
                                  display: 'inline-block',
                                  padding: '4px 10px',
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 800,
                                }}
                              >
                                {status.label} · {status.detail}
                              </span>
                            </td>
                            <td style={{ padding: '12px 8px', fontSize: 12, color: COLORS.gray[600] }}>
                              {record?.lastInspectionDate ? (
                                <div>Last passed: {formatDateShort(record.lastInspectionDate)}</div>
                              ) : null}
                              {record?.lastCheckedAt ? (
                                <div style={{ color: COLORS.gray[400] }}>
                                  Checked: {formatDateShort(record.lastCheckedAt)} ({record.source})
                                </div>
                              ) : (
                                <span style={{ color: COLORS.gray[400] }}>Never checked</span>
                              )}
                              {record?.notes && (
                                <div style={{ color: COLORS.gray[700], marginTop: 2 }}>
                                  {record.notes}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                              <div
                                style={{
                                  display: 'inline-flex',
                                  gap: 6,
                                  flexWrap: 'wrap',
                                  justifyContent: 'flex-end',
                                }}
                              >
                                {canEdit && (
                                  <Button
                                    type="button"
                                    disabled={isSyncing}
                                    onClick={() => handleSyncSingleCarFromGreenway(car)}
                                    style={{
                                      padding: '5px 10px',
                                      fontSize: 12,
                                      background: '#dcfce7',
                                      color: '#166534',
                                      border: '1px solid #86efac',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 5,
                                    }}
                                    title="Fetch inspection deadline from greenway.ge by license plate"
                                  >
                                    <RefreshCw size={12} />
                                    <span>{isSyncing ? 'Checking…' : 'Sync Greenway'}</span>
                                  </Button>
                                )}

                                {canEdit && (
                                  <Button
                                    type="button"
                                    onClick={() => handleOpenEditGreenwayModal(car)}
                                    style={{
                                      padding: '5px 9px',
                                      fontSize: 12,
                                      background: '#ffffff',
                                    }}
                                    title="Set or edit deadline manually"
                                  >
                                    <Pencil size={12} />
                                    <span style={{ marginLeft: 4 }}>Set Date</span>
                                  </Button>
                                )}

                                <a
                                  href={
                                    cleanPlate
                                      ? `https://greenway.ge/en/booking?vehicleLicensePlate=${cleanPlate}`
                                      : 'https://greenway.ge/en/'
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => {
                                    if (cleanPlate) {
                                      navigator.clipboard?.writeText(cleanPlate).catch(() => {});
                                    }
                                  }}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    padding: '5px 9px',
                                    borderRadius: 8,
                                    border: `1px solid ${COLORS.gray[300]}`,
                                    background: '#ffffff',
                                    color: COLORS.gray[800],
                                    fontSize: 12,
                                    fontWeight: 700,
                                    textDecoration: 'none',
                                  }}
                                  title="Open Greenway.ge (copies plate to clipboard)"
                                >
                                  <span>Greenway.ge</span>
                                  <ExternalLink size={12} />
                                </a>

                                <Button
                                  type="button"
                                  onClick={() =>
                                    sendMaintenanceReminderEmail({
                                      itemsToRemind: [
                                        {
                                          carName: car.name,
                                          plate: car.plate,
                                          type: 'Greenway Technical Inspection (PTI)',
                                          statusLabel: `${status.label} (${status.detail})`,
                                          dueDate: record?.deadlineDate
                                            ? formatDateShort(record.deadlineDate)
                                            : 'Check on greenway.ge',
                                          checkUrl: cleanPlate
                                            ? `https://greenway.ge/en/booking?vehicleLicensePlate=${cleanPlate}`
                                            : 'https://greenway.ge/en/',
                                          notes:
                                            record?.notes ||
                                            'Please verify PTI status at https://greenway.ge/en/',
                                        },
                                      ],
                                      reasonTitle: `Greenway PTI Check for ${car.name} (${car.plate})`,
                                    })
                                  }
                                  style={{
                                    padding: '5px 9px',
                                    fontSize: 12,
                                    background: '#ffffff',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                  }}
                                  title="Send email reminder to check this car on Greenway"
                                >
                                  <Mail size={12} />
                                  <span>Remind</span>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}

        {/* =====================================================================
            TAB 3: CALENDAR MAINTENANCE SCHEDULE (MOVED FROM CARSPAGE)
           ===================================================================== */}
        {activeTab === 'schedule' && (
          <section style={cardStyle()}>
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: COLORS.gray[900] }}>
                Calendar Maintenance Schedule (Block Car Availability)
              </h4>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: COLORS.gray[600] }}>
                Use this section when a car needs to be blocked out on the booking calendar for
                repairs or shop visits.{' '}
                {!isAdmin && canEdit && 'Changes by non-admins are submitted for admin approval.'}
              </p>
            </div>

            <Panel>
              <div
                style={{
                  display: 'grid',
                  gap: 14,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  alignItems: 'flex-end',
                }}
              >
                <Field label="Select Car">
                  <Select
                    value={scheduleCarId}
                    onChange={(e) => setScheduleCarId(e.target.value)}
                  >
                    <option value="">Select car…</option>
                    {cars
                      .filter((c) => c.active !== false)
                      .map((car) => (
                        <option key={car.id} value={car.id}>
                          {car.name} ({car.plate || car.id})
                        </option>
                      ))}
                  </Select>
                </Field>

                <div>
                  <Button
                    type="button"
                    disabled={!activeCompanyId || !scheduleCarId || scheduleLoading}
                    onClick={() => loadCalendarSchedule(activeCompanyId, scheduleCarId)}
                    style={{ background: '#fff' }}
                  >
                    {scheduleLoading ? 'Refreshing…' : 'Refresh Schedule'}
                  </Button>
                </div>
              </div>
            </Panel>

            {scheduleCarId && (
              <>
                <Panel
                  title={
                    scheduleEditingId
                      ? 'Edit Calendar Maintenance Block'
                      : 'Add Calendar Maintenance Block'
                  }
                >
                  <form
                    onSubmit={handleSubmitCalendarSchedule}
                    style={{
                      display: 'grid',
                      gap: 12,
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Field label="Title / Description">
                      <Input
                        value={scheduleForm.title}
                        onChange={(e) =>
                          setScheduleForm((f) => ({ ...f, title: e.target.value }))
                        }
                        placeholder="Shop repair, bodywork, inspection…"
                        disabled={!canEdit}
                      />
                    </Field>

                    <Field label="Start">
                      <Input
                        type="datetime-local"
                        value={scheduleForm.start}
                        onChange={(e) =>
                          setScheduleForm((f) => ({ ...f, start: e.target.value }))
                        }
                        disabled={!canEdit}
                      />
                    </Field>

                    <Field label="End">
                      <Input
                        type="datetime-local"
                        value={scheduleForm.end}
                        onChange={(e) =>
                          setScheduleForm((f) => ({ ...f, end: e.target.value }))
                        }
                        disabled={!canEdit}
                      />
                    </Field>

                    <Field label="Notes">
                      <Textarea
                        rows={2}
                        value={scheduleForm.notes}
                        onChange={(e) =>
                          setScheduleForm((f) => ({ ...f, notes: e.target.value }))
                        }
                        placeholder="Extra info…"
                        disabled={!canEdit}
                      />
                    </Field>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <Button type="submit" disabled={!canEdit}>
                        {scheduleEditingId ? 'Save Changes' : 'Block Calendar Dates'}
                      </Button>
                      {scheduleEditingId && (
                        <Button
                          type="button"
                          onClick={() => {
                            setScheduleEditingId(null);
                            setScheduleForm({ title: '', start: '', end: '', notes: '' });
                          }}
                          style={{ background: '#fff' }}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                </Panel>

                <Panel title="Scheduled Calendar Blocks">
                  {scheduleItems.length === 0 ? (
                    <div style={{ fontSize: 13, color: COLORS.gray[500] }}>
                      No calendar maintenance blocks for this car.
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 13,
                        }}
                      >
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                            <th style={{ textAlign: 'left', padding: 6 }}>Title</th>
                            <th style={{ textAlign: 'left', padding: 6 }}>Start</th>
                            <th style={{ textAlign: 'left', padding: 6 }}>End</th>
                            <th style={{ textAlign: 'left', padding: 6 }}>Notes</th>
                            <th style={{ textAlign: 'left', padding: 6 }}>Approval</th>
                            {canEdit && (
                              <th style={{ textAlign: 'left', padding: 6 }}>Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {scheduleItems.map((m) => (
                            <tr
                              key={m.id}
                              style={{ borderBottom: `1px solid ${COLORS.gray[100]}` }}
                            >
                              <td style={{ padding: 6, fontWeight: 600 }}>{m.title}</td>
                              <td style={{ padding: 6 }}>{formatDateTime(m.start)}</td>
                              <td style={{ padding: 6 }}>{formatDateTime(m.end)}</td>
                              <td style={{ padding: 6 }}>{m.notes}</td>
                              <td style={{ padding: 6 }}>{m.approvalStatus}</td>
                              {canEdit && (
                                <td style={{ padding: 6 }}>
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <Button
                                      type="button"
                                      onClick={() => {
                                        setScheduleEditingId(m.id);
                                        setScheduleForm({
                                          title: m.title || '',
                                          start: m.start ? m.start.slice(0, 16) : '',
                                          end: m.end ? m.end.slice(0, 16) : '',
                                          notes: m.notes || '',
                                        });
                                      }}
                                      style={{ padding: '4px 7px', background: '#fff' }}
                                    >
                                      <Pencil size={13} />
                                    </Button>
                                    <DangerButton
                                      type="button"
                                      onClick={() => handleDeleteCalendarSchedule(m.id)}
                                      style={{ padding: '4px 7px' }}
                                    >
                                      <Trash2 size={13} />
                                    </DangerButton>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
              </>
            )}
          </section>
        )}
      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            zIndex: 9999,
            maxWidth: 380,
          }}
        >
          <div
            style={{
              borderRadius: 12,
              padding: '12px 16px',
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              boxShadow: '0 10px 30px rgba(15,23,42,0.2)',
              background:
                toast.variant === 'success'
                  ? '#d1fae5'
                  : toast.variant === 'error'
                    ? '#fee2e2'
                    : '#f0f9ff',
              color:
                toast.variant === 'success'
                  ? '#065f46'
                  : toast.variant === 'error'
                    ? '#7f1d1d'
                    : '#0c4a6e',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <div>
              {toast.variant === 'success' ? '✓' : toast.variant === 'error' ? '✕' : 'ℹ'}
            </div>
            <div style={{ flex: 1 }}>{toast.message}</div>
          </div>
        </div>
      )}
    </div>
  );
}
